import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Layout, Typography, Button, Card, Spin, Alert, App, Form, Input, InputNumber,
  Select, DatePicker, Row, Col, Tag, Space, Checkbox, Modal, Radio, Divider, Tooltip, AutoComplete,
  Dropdown, List, Empty, Drawer, Segmented, FloatButton,
} from 'antd';
import {
  ArrowLeftOutlined, PlusOutlined, DeleteOutlined, EditOutlined, CopyOutlined,
  SaveOutlined, CheckCircleOutlined, ShoppingCartOutlined, BarcodeOutlined, LineChartOutlined,
  AudioOutlined, SwapOutlined, SearchOutlined, AppstoreOutlined, ArrowUpOutlined,
  ArrowDownOutlined, DownOutlined, RightOutlined, MoreOutlined, LinkOutlined,
  ClockCircleOutlined, DollarOutlined, QuestionCircleOutlined, MinusOutlined, CheckOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useMediaQuery } from 'react-responsive';
import {
  getListaCompra, updateListaCompra, deleteListaCompra, duplicarListaCompra,
  salvarListaComoModelo, getPreviewLancamento, lancarListaCompra, concluirListaCompra,
  createItemLista, updateItemLista, deleteItemLista, UNIDADES, PRIORIDADES,
  getProdutoPorGtin, contribuirProdutoCatalogo, getSugestaoPreco, getProdutosCompra,
  getProdutosRecorrentes,
  getComparadorMercado, getAlertaOrcamento, getAlertasPreco, getLocaisCompra,
  getSecoesCompra, createSecaoCompra, updateSecaoCompra, deleteSecaoCompra,
  reordenarSecoesCompra, criarListaPendentes,
  getPreviewLancamentoItem, lancarItem as lancarItemApi, verificarPrecoItem,
} from '../../services/comprasService';
import { getCategorias, getContas, getCartoes, getFaturasDisponiveis } from '../../services/financeService';
import { getErrorMessage } from '../../utils/errorHandler';
import { currencyInputProps, decimalInputProps } from '../../utils/numberInputMask';
import BarcodeScanner from '../../components/BarcodeScanner/BarcodeScanner';

const MONEY = currencyInputProps({ inputMode: 'decimal' });
const SpeechRecognition =
  typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

const { Content } = Layout;
const { Title, Text } = Typography;

const brl = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const fmtData = (d) => (d ? dayjs(String(d).slice(0, 10)).format('DD/MM/YYYY') : '—');
const emReflexao = (item) =>
  !!item.disponivel_em && dayjs(String(item.disponivel_em).slice(0, 10)).isAfter(dayjs(), 'day');
const semAcento = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const SEM_SETOR = '__sem_setor__';
// Palavras "de verdade" do nome, sem acento e sem plural simples ("bananas" ≈ "banana").
const palavrasProduto = (s) => semAcento(s).split(/[^a-z0-9]+/)
  .filter((w) => w.length >= 3)
  .map((w) => (w.length > 3 && w.endsWith('s') ? w.slice(0, -1) : w));
const PRIORIDADE_ORDEM = { ALTA: 0, MEDIA: 1, BAIXA: 2 };

// Só peso/volume aceitam fração; as demais unidades são sempre números inteiros.
const UNIDADES_FRACIONARIAS = new Set(['KG', 'L']);
// Fracionárias (kg/L): mesma máscara do dinheiro — só dígitos, vírgula fixa — pra não
// misturar ponto (formatação padrão) com o teclado numérico do celular (só tem vírgula).
const qtdInputProps = (unidade) =>
  UNIDADES_FRACIONARIAS.has(unidade)
    ? decimalInputProps(3, { min: 0 })
    : { min: 0, step: 1, precision: 0, inputMode: 'numeric' };

const PRECO_UNIDADE_LABEL = {
  KG: 'por kg', G: 'por g', L: 'por L', ML: 'por ml', CX: 'por cx', PCT: 'por pct', DZ: 'por dz',
};
const precoUnidadeLabel = (u) => PRECO_UNIDADE_LABEL[u] || 'por unidade';
const round2 = (n) => Math.round(n * 100) / 100;
const capStyle = { fontSize: 10, color: '#8c8c8c', textAlign: 'center', marginTop: 2 };

const STATUS_TAG = {
  RASCUNHO: { color: 'default', label: 'Rascunho' },
  EM_COMPRA: { color: 'processing', label: 'Em compra' },
  CONCLUIDA: { color: 'gold', label: 'Concluída' },
  LANCADA: { color: 'green', label: 'Lançada' },
  CANCELADA: { color: 'red', label: 'Cancelada' },
};

const STATUS_FATURA = { ABERTA: 'Aberta', FECHADA: 'Fechada', PAGA: 'Paga' };

function ListaCompraDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const [lista, setLista] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modoCompra, setModoCompra] = useState(false);
  // Painel de números (previsto/diferença/caixa) como bottom sheet no Modo Compra.
  const [painelOpen, setPainelOpen] = useState(false);
  // Filtro da lista no Modo Compra: o padrão é "só o que falta pegar" — misturar pegos
  // e pendentes fazia o usuário reler a lista inteira a cada corredor.
  const [filtro, setFiltro] = useState('PENDENTES');
  // Item cujo campo de preço deve receber foco: marcar como pego já abre o teclado
  // numérico na linha, transformando "marcar + achar o preço" em um gesto só.
  const [focoPrecoId, setFocoPrecoId] = useState(null);
  // Conferência do cupom: preenche os valores dos itens que só foram precificados no
  // caixa (hortifrúti pesado no checkout).
  const [cupomOpen, setCupomOpen] = useState(false);
  const [cupomValores, setCupomValores] = useState({});
  const [cupomSalvando, setCupomSalvando] = useState(false);

  const [categorias, setCategorias] = useState([]);
  const [contas, setContas] = useState([]);
  const [cartoes, setCartoes] = useState([]);
  const [locais, setLocais] = useState([]);
  const [secoes, setSecoes] = useState([]);
  const [busca, setBusca] = useState('');
  const [secoesOpen, setSecoesOpen] = useState(false);
  const [novoSetor, setNovoSetor] = useState('');
  const [gruposFechados, setGruposFechados] = useState(() => new Set());
  const [faturas, setFaturas] = useState([]);
  const [faturasLoading, setFaturasLoading] = useState(false);

  const [addForm] = Form.useForm();
  const [addGtin, setAddGtin] = useState('');
  const [addAberto, setAddAberto] = useState(false); // no modo compra o bloco começa recolhido
  const [addExtrasAberto, setAddExtrasAberto] = useState(false);
  const [produtoOptions, setProdutoOptions] = useState([]);
  const [nomeBusca, setNomeBusca] = useState('');
  const [produtosFrequentes, setProdutosFrequentes] = useState([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [alertaOrc, setAlertaOrc] = useState(null);
  const [comparador, setComparador] = useState(null);
  const [comparadorOpen, setComparadorOpen] = useState(false);
  const [sugestaoMercado, setSugestaoMercado] = useState(null);
  const [sugestaoFechada, setSugestaoFechada] = useState(false);
  const [totalCaixa, setTotalCaixa] = useState(null);
  const [alertasPreco, setAlertasPreco] = useState([]);
  const [localGenerico, setLocalGenerico] = useState(null);
  const [ouvindo, setOuvindo] = useState(false);
  const recognitionRef = useRef(null);
  const vozDisponivel = !!SpeechRecognition;
  // "Substituir item": troca o genérico ("Arroz") pelo produto que foi de fato pego
  // ("Arroz Tio João 5kg") mantendo quantidade, unidade e setor — sem duplicar a linha.
  const [substItem, setSubstItem] = useState(null);
  const [substGtin, setSubstGtin] = useState('');
  const [substForm] = Form.useForm();
  const [scannerAlvo, setScannerAlvo] = useState('add'); // 'add' | 'substituir'
  const [editItem, setEditItem] = useState(null);
  const [itemNovoId, setItemNovoId] = useState(null);
  const [editForm] = Form.useForm();
  const [lancarOpen, setLancarOpen] = useState(false);
  const [lancarForm] = Form.useForm();
  const [itemLancarAlvo, setItemLancarAlvo] = useState(null);
  const [itemLancarOpen, setItemLancarOpen] = useState(false);
  const [itemLancarForm] = Form.useForm();
  const [itemFaturas, setItemFaturas] = useState([]);
  const [itemFaturasLoading, setItemFaturasLoading] = useState(false);
  const [ignorarReflexaoAtual, setIgnorarReflexaoAtual] = useState(false);
  const [verificarItem, setVerificarItem] = useState(null);
  const [verificarForm] = Form.useForm();
  const [metaOpen, setMetaOpen] = useState(false);
  const [metaForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [modal, modalCtx] = Modal.useModal();
  const addNomeRef = useRef(null);

  const bloqueada = lista && ['LANCADA', 'CANCELADA'].includes(lista.status);
  const ehMarketplace = lista?.local_tipo === 'MARKETPLACE';
  const itens = useMemo(() => lista?.itens || [], [lista]);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const podeFinalizarNoModo = !!lista && !lista.eh_modelo && lista.status !== 'LANCADA';
  const podeSoConcluirNoModo = !!lista && !lista.eh_modelo && !['LANCADA', 'CONCLUIDA'].includes(lista.status);

  const fetchLista = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getListaCompra(id);
      setLista(data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Lista não encontrada.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLista();
    getCategorias().then((d) => setCategorias(Array.isArray(d) ? d : d?.results || [])).catch(() => {});
    getContas().then((d) => setContas(Array.isArray(d) ? d : d?.results || [])).catch(() => {});
    getCartoes().then((d) => setCartoes(Array.isArray(d) ? d : d?.results || [])).catch(() => {});
    getAlertaOrcamento(id).then((d) => setAlertaOrc(d && d.categoria ? d : null)).catch(() => {});
    getLocaisCompra().then(setLocais).catch(() => {});
    getSecoesCompra().then(setSecoes).catch(() => {});
  }, [fetchLista, id]);

  const recarregarSecoes = useCallback(() => {
    getSecoesCompra().then(setSecoes).catch(() => {});
  }, []);

  const definirLocal = async (localId) => {
    try {
      const atualizada = await updateListaCompra(id, { local: localId });
      setLista(atualizada);
      message.success('Local definido.');
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao definir o local.'));
    }
  };

  // Recarrega alerta de orçamento e de preço acima da média quando os totais mudam
  useEffect(() => {
    if (!lista || lista.eh_modelo) return;
    getAlertaOrcamento(id).then((d) => setAlertaOrc(d && d.categoria ? d : null)).catch(() => {});
    getAlertasPreco(id).then(setAlertasPreco).catch(() => {});
  }, [id, lista?.total_estimado, lista?.total_final]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lista sem loja (ou com loja genérica): sugere, sem precisar abrir o comparador, o
  // mercado onde essa cesta tende a sair mais barata. O backend decide se vale o aviso.
  const semLojaEspecifica = !!lista && (!lista.local || lista.local_eh_generico);
  useEffect(() => {
    if (!lista || lista.eh_modelo || ehMarketplace || bloqueada || !semLojaEspecifica || itens.length < 3) {
      setSugestaoMercado(null);
      return;
    }
    getComparadorMercado(id).then((c) => setSugestaoMercado(c?.sugestao || null)).catch(() => {});
  }, [id, semLojaEspecifica, itens.length, lista?.total_estimado]); // eslint-disable-line react-hooks/exhaustive-deps

  const alertaPrecoPorItem = useMemo(() => {
    const m = {};
    alertasPreco.forEach((a) => { m[a.item_id] = a; });
    return m;
  }, [alertasPreco]);

  const abrirComparador = async () => {
    try {
      const c = await getComparadorMercado(id);
      setComparador(c);
      setComparadorOpen(true);
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao comparar mercados.'));
    }
  };

  const despesaCategorias = useMemo(
    () => categorias.filter((c) => c.tipo === 'despesa'),
    [categorias],
  );

  const resumo = useMemo(() => {
    const pendentes = itens.filter((i) => !i.comprado).length;
    const comprados = itens.length - pendentes;
    const naoEncontrados = itens.filter((i) => !i.comprado && i.indisponivel).length;
    // Pego, mas o valor só sai no caixa (hortifrúti pesado no checkout). O backend soma
    // R$ 0,00 desses itens (`ItemLista.subtotal_final`), então eles ficam contados à
    // parte: o total não pode parecer fechado enquanto faltar preencher o cupom.
    const aguardandoValor = itens.filter((i) => i.comprado && i.preco_unitario == null).length;
    // Diferença "maçã com maçã": só nos itens já pegos, pago vs. previsto — e só quando
    // todos eles têm estimativa E preço pago (senão o pago ficaria artificialmente baixo,
    // já que item aguardando valor entra como zero no total_final).
    const difComprados =
      comprados > 0 &&
      aguardandoValor === 0 &&
      lista?.total_previsto_comprados != null &&
      itens.every((i) => !i.comprado || i.preco_estimado != null)
        ? Number(lista.total_final) - Number(lista.total_previsto_comprados)
        : null;
    return { total: itens.length, pendentes, comprados, naoEncontrados, aguardandoValor, difComprados };
  }, [itens, lista?.total_final, lista?.total_previsto_comprados]);

  // Marketplace: itens são lançados um a um, em datas diferentes — "CONFIRMADO" (total
  // da lista inteira) não serve pra isso, já que um item lançado individualmente zera no
  // total da lista (evita contar 2x). "Já gasto" soma só os itens de fato lançados.
  const resumoMarketplace = useMemo(() => {
    if (!ehMarketplace) return null;
    let jaGasto = 0;
    let lancados = 0;
    itens.forEach((i) => {
      if (!i.transacao) return;
      lancados += 1;
      jaGasto += (Number(i.quantidade) || 0) * (Number(i.preco_unitario) || 0);
    });
    return { jaGasto, lancados };
  }, [itens, ehMarketplace]);

  const buscaAtiva = busca.trim().length > 0;

  // Resultado da busca por produto na lista (ignora acento/caixa; nome + observação).
  const itensBusca = useMemo(() => {
    if (!buscaAtiva) return [];
    const q = semAcento(busca.trim());
    return itens.filter(
      (i) => semAcento(i.nome_produto).includes(q) || semAcento(i.observacao).includes(q),
    );
  }, [itens, busca, buscaAtiva]);

  // Itens agrupados por setor (corredor), na ordem dos corredores; dentro de cada
  // setor os já pegos vão para o fim. Setor "Sem setor" fica por último.
  // Em listas de marketplace, corredor não existe — agrupa por PRIORIDADE em vez
  // disso (Alta → Média → Baixa → sem prioridade), o mesmo efeito visual que já
  // existe pros corredores, dando um resultado concreto a quem marca prioridade.
  // Filtro do Modo Compra. O padrão ("A pegar") é o que resolve a pergunta do corredor —
  // "o que ainda falta?" — sem obrigar a reler a lista inteira. O item recém-marcado fica
  // visível enquanto o preço dele está em foco, senão sumiria debaixo do teclado.
  const itensFiltrados = useMemo(() => {
    if (!modoCompra || filtro === 'TODOS') return itens;
    if (filtro === 'PEGOS') return itens.filter((i) => i.comprado);
    if (filtro === 'FORA') return itens.filter((i) => !i.comprado && i.indisponivel);
    return itens.filter((i) => (!i.comprado && !i.indisponivel) || i.id === focoPrecoId);
  }, [itens, modoCompra, filtro, focoPrecoId]);

  const grupos = useMemo(() => {
    const buckets = new Map();
    itensFiltrados.forEach((i) => {
      const key = (ehMarketplace ? i.prioridade : i.secao) || SEM_SETOR;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(i);
    });
    const ordemPorId = new Map(secoes.map((s) => [s.id, s.ordem]));
    const nomePorId = new Map(secoes.map((s) => [s.id, s.nome]));
    const arr = [...buckets.entries()].map(([key, lista]) => {
      const pendentes = lista.filter((i) => !i.comprado);
      const pegos = lista.filter((i) => i.comprado);
      const semGrupo = key === SEM_SETOR;
      const nome = semGrupo
        ? (ehMarketplace ? 'Sem prioridade' : 'Sem setor')
        : ehMarketplace
        ? `Prioridade ${(PRIORIDADES.find((p) => p.value === key) || {}).label?.toLowerCase() || key}`
        : nomePorId.get(key) || lista[0]?.secao_nome || 'Setor';
      const ordem = semGrupo
        ? Number.MAX_SAFE_INTEGER
        : ehMarketplace
        ? PRIORIDADE_ORDEM[key] ?? 9
        : ordemPorId.get(key) ?? lista[0]?.secao_ordem ?? 9999;
      return {
        key: String(key),
        nome,
        ordem,
        itens: [...pendentes, ...pegos],
        pendentes: pendentes.length,
        total: lista.length,
      };
    });
    arr.sort((a, b) => a.ordem - b.ordem || a.nome.localeCompare(b.nome));
    return arr;
  }, [itensFiltrados, secoes, ehMarketplace]);

  const agrupar = grupos.length > 1;
  const toggleGrupo = (key) =>
    setGruposFechados((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // No Modo Compra a tela vira uma ferramenta de corredor: o FAB global de lançamento
  // financeiro (fixo no canto, sem consciência de rota) cobria o botão de finalizar,
  // então some enquanto o modo está ativo. Wake Lock mantém a tela acesa — empurrando
  // o carrinho ninguém quer destravar o celular a cada item.
  useEffect(() => {
    if (!modoCompra) return undefined;
    document.body.classList.add('sa-modo-compra');
    let sentinel = null;
    let cancelado = false;
    navigator.wakeLock?.request('screen')
      .then((s) => { if (cancelado) s.release().catch(() => {}); else sentinel = s; })
      .catch(() => {});
    return () => {
      cancelado = true;
      document.body.classList.remove('sa-modo-compra');
      sentinel?.release().catch(() => {});
    };
  }, [modoCompra]);

  // --- Itens: mutações otimistas simples (refetch após salvar) ---
  const patchItemLocal = (itemId, patch) => {
    setLista((prev) => ({
      ...prev,
      itens: prev.itens.map((i) => (i.id === itemId ? { ...i, ...patch } : i)),
    }));
  };

  // Depois de concluir/lançar: se sobrou item não comprado (não encontrado, caro
  // demais ou só não alcançado), oferece já deixar pronta a próxima lista com eles.
  const ofertarListaPendentes = (listaAtualizada) => {
    const pendentes = (listaAtualizada.itens || []).filter((i) => !i.comprado);
    if (pendentes.length === 0) return;
    modal.confirm({
      title: `${pendentes.length} ${pendentes.length === 1 ? 'item ficou' : 'itens ficaram'} de fora`,
      content: 'Não encontrado, caro demais ou só não alcançado — quer já deixar pronta uma lista com eles pra próxima ida ao mercado?',
      okText: 'Criar lista',
      cancelText: 'Agora não',
      onOk: async () => {
        try {
          const nova = await criarListaPendentes(listaAtualizada.id);
          message.success('Lista criada com os itens pendentes.');
          navigate(`/listas-compras/${nova.id}`);
        } catch (err) {
          message.error(getErrorMessage(err, 'Erro ao criar lista.'));
        }
      },
    });
  };

  const salvarItemCampo = async (item, patch) => {
    patchItemLocal(item.id, patch);
    try {
      await updateItemLista(item.id, patch);
      const atualizada = await getListaCompra(id);
      setLista(atualizada);
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao atualizar item.'));
      fetchLista();
    }
  };

  // Item genérico ainda pendente que o produto novo "detalha": todas as palavras do
  // item da lista aparecem no nome/marca do novo ("Arroz" ⊂ "Arroz Branco Tio João").
  // Só considera itens sem código de barras (os já identificados não são genéricos).
  // Havendo mais de um, fica com o mais específico (mais palavras em comum).
  const acharItemGenerico = (nome, marca) => {
    const alvo = new Set(palavrasProduto(`${nome} ${marca || ''}`));
    const nomeNorm = semAcento(nome).trim();
    let melhor = null;
    let melhorN = 0;
    itens.forEach((i) => {
      if (i.comprado || i.gtin || i.transacao) return;
      if (semAcento(i.nome_produto).trim() === nomeNorm) return;
      const ws = palavrasProduto(i.nome_produto);
      if (ws.length && ws.every((w) => alvo.has(w)) && ws.length > melhorN) {
        melhor = i;
        melhorN = ws.length;
      }
    });
    return melhor;
  };

  const limparAddForm = () => {
    addForm.resetFields();
    addForm.setFieldsValue?.({ quantidade: 1, unidade: 'UN' });
    setAddGtin('');
    setNomeBusca('');
    setProdutoOptions([]);
  };

  // Troca a identidade do item (nome/marca/tamanho/código) e preserva o resto:
  // quantidade, unidade, setor, observação e o preço previsto (o plano não muda).
  const substituirItem = async (item, dados) => {
    const payload = {
      nome_produto: dados.nome_produto.trim(),
      marca: (dados.marca || '').trim(),
      conteudo: (dados.conteudo || '').trim(),
      gtin: dados.gtin || '',
      ...(item.indisponivel ? { indisponivel: '' } : {}),
      ...(dados.extra || {}),
    };
    await updateItemLista(item.id, payload);
    const atualizada = await getListaCompra(id);
    setLista(atualizada);
    message.success(`"${item.nome_produto}" substituído por "${payload.nome_produto}"`);
    setItemNovoId(item.id);
    setTimeout(() => setItemNovoId((cur) => (cur === item.id ? null : cur)), 2600);
    if (payload.comprado) navigator.vibrate?.(12);
  };

  const adicionarItem = async (values) => {
    try {
      const preco = values.preco_estimado ?? null;
      // No Modo Compra você adiciona o item porque acabou de pegá-lo: o preço digitado
      // já é o preço pago e o item entra marcado como comprado (sem digitar de novo).
      const jaNoCarrinho = modoCompra && preco != null;
      const nome = values.nome_produto.trim();

      // Escaneou (ou, no Modo Compra, digitou) o produto específico de um item genérico
      // que já está na lista → oferece substituir em vez de criar uma linha duplicada.
      const generico = (addGtin || modoCompra) && !ehMarketplace
        ? acharItemGenerico(nome, values.marca)
        : null;
      if (generico) {
        const escolha = await new Promise((resolve) => {
          modal.confirm({
            title: `Substituir "${generico.nome_produto}"?`,
            icon: <SwapOutlined />,
            content: `"${nome}" parece ser o "${generico.nome_produto}" que já está na lista. Substituir mantém a quantidade e o setor e evita o item duplicado.`,
            okText: 'Substituir',
            cancelText: 'Adicionar como novo',
            closable: false,
            maskClosable: false,
            onOk: () => resolve('substituir'),
            onCancel: () => resolve('adicionar'),
          });
        });
        if (escolha === 'substituir') {
          const qtdMudou = values.quantidade != null && values.quantidade !== 1;
          await substituirItem(generico, {
            nome_produto: nome,
            marca: values.marca,
            conteudo: values.conteudo,
            gtin: addGtin,
            extra: {
              ...(qtdMudou ? { quantidade: values.quantidade, unidade: values.unidade || 'UN' } : {}),
              ...(jaNoCarrinho ? { preco_unitario: preco, comprado: true } : {}),
              ...(!modoCompra && preco != null ? { preco_estimado: preco } : {}),
            },
          });
          limparAddForm();
          addNomeRef.current?.focus();
          return;
        }
      }
      const payload = {
        lista: Number(id),
        nome_produto: nome,
        marca: (values.marca || '').trim(),
        conteudo: (values.conteudo || '').trim(),
        quantidade: values.quantidade ?? 1,
        unidade: values.unidade || 'UN',
        preco_estimado: preco,
        preco_unitario: jaNoCarrinho ? preco : null,
        comprado: jaNoCarrinho,
        gtin: addGtin || '',
      };
      if (ehMarketplace) {
        payload.link_produto = (values.link_produto || '').trim();
        payload.prioridade = values.prioridade || '';
        payload.disponivel_em = values.disponivel_em ? values.disponivel_em.format('YYYY-MM-DD') : null;
      }
      const criado = await createItemLista(payload);
      limparAddForm();
      const atualizada = await getListaCompra(id);
      setLista(atualizada);
      addNomeRef.current?.focus();
      const n = atualizada?.itens?.length ?? 0;
      message.success(
        `"${nome}" ${jaNoCarrinho ? 'adicionado ao carrinho' : 'adicionado à lista'}${n ? ` · ${n} ${n === 1 ? 'item' : 'itens'}` : ''}`,
      );
      if (criado?.id) {
        setItemNovoId(criado.id);
        setTimeout(() => setItemNovoId((cur) => (cur === criado.id ? null : cur)), 2600);
      }
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao adicionar item.'));
    }
  };

  // Fora do Modo Compra: preenche o preço estimado a partir do histórico (o usuário não
  // chuta valor). No Modo Compra: só mostra a referência — o valor digitado será o preço
  // pago de verdade, então não pré-preenchemos para não gravar um preço errado.
  const sugerirPreco = async ({ nome, gtin }) => {
    if (addForm.getFieldValue('preco_estimado') != null) return;
    try {
      const s = await getSugestaoPreco({ lista: id, nome, gtin });
      const valor = s?.preco_sugerido ?? s?.preco_estimado;
      if (valor == null) return;
      const origem = {
        media_local: 'média neste local',
        ultimo_local: 'última compra neste local',
        media: `média (${s.local})`,
        ultimo: `última compra no ${s.local}`,
      }[s.base_sugestao] || 'histórico';
      if (modoCompra) {
        message.info(`Referência (${origem}): ${brl(valor)}. Digite o preço que está pagando.`);
      } else {
        addForm.setFieldsValue({ preco_estimado: Number(valor) });
        message.info(`Preço estimado pelo ${origem}.`);
      }
    } catch { /* silencioso */ }
  };

  // Autocomplete do nome do produto — igual ao que já existe na descrição dos
  // lançamentos: busca (com debounce) nos produtos que o usuário já comprou antes.
  useEffect(() => {
    if (nomeBusca.trim().length < 2) {
      setProdutoOptions([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const resultados = await getProdutosCompra({ busca: nomeBusca.trim() });
        setProdutoOptions(
          resultados.slice(0, 8).map((p) => ({
            value: p.nome,
            unidade: p.unidade,
            label: (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.nome}
                </span>
                <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>
                  {p.compras}x · {brl(p.ultimo?.preco)}
                </Text>
              </div>
            ),
          })),
        );
      } catch {
        setProdutoOptions([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [nomeBusca]);

  // "Produtos frequentes" — o que o usuário já comprou OU já colocou antes em outras
  // listas de supermercado/atacadista (mesmo sem concluir a compra), pra adicionar na
  // lista atual com 1 toque em vez de digitar de novo o que já é rotina.
  useEffect(() => {
    getProdutosRecorrentes(id)
      .then((resultados) => setProdutosFrequentes(resultados))
      .catch(() => setProdutosFrequentes([]));
  }, [id]);

  const nomesNaLista = useMemo(
    () => new Set(itens.map((i) => (i.nome_produto || '').trim().toLowerCase())),
    [itens],
  );
  const produtosDisponiveis = useMemo(
    () => produtosFrequentes.filter((p) => !nomesNaLista.has((p.nome || '').trim().toLowerCase())),
    [produtosFrequentes, nomesNaLista],
  );
  const sugestoesFrequentes = useMemo(() => produtosDisponiveis.slice(0, 12), [produtosDisponiveis]);

  const adicionarProdutoFrequente = (produto) => {
    adicionarItem({ nome_produto: produto.nome, quantidade: 1, unidade: produto.unidade || 'UN' });
  };

  // Modal "Ver todos" — busca + seleção múltipla, pra quando os 12 chips não bastam.
  const [produtosModalOpen, setProdutosModalOpen] = useState(false);
  const [produtosBusca, setProdutosBusca] = useState('');
  const [produtosSelecionados, setProdutosSelecionados] = useState(new Set());
  const [produtosQuantidades, setProdutosQuantidades] = useState({});
  const [adicionandoVarios, setAdicionandoVarios] = useState(false);

  const produtosModalFiltrados = useMemo(() => {
    const termo = produtosBusca.trim().toLowerCase();
    if (!termo) return produtosDisponiveis;
    return produtosDisponiveis.filter((p) => (p.nome || '').toLowerCase().includes(termo));
  }, [produtosDisponiveis, produtosBusca]);

  const toggleProdutoSelecionado = (chave) => {
    setProdutosSelecionados((prev) => {
      const next = new Set(prev);
      next.has(chave) ? next.delete(chave) : next.add(chave);
      return next;
    });
  };

  const fecharProdutosModal = () => {
    setProdutosModalOpen(false);
    setProdutosBusca('');
    setProdutosSelecionados(new Set());
    setProdutosQuantidades({});
  };

  const adicionarVariosProdutos = async () => {
    const produtos = produtosDisponiveis.filter((p) => produtosSelecionados.has(p.chave));
    if (produtos.length === 0) return;
    setAdicionandoVarios(true);
    try {
      await Promise.all(produtos.map((p) => createItemLista({
        lista: Number(id),
        nome_produto: p.nome,
        quantidade: produtosQuantidades[p.chave] || 1,
        unidade: p.unidade || 'UN',
      })));
      const atualizada = await getListaCompra(id);
      setLista(atualizada);
      message.success(`${produtos.length} ${produtos.length === 1 ? 'produto adicionado' : 'produtos adicionados'} à lista.`);
      setProdutosModalOpen(false);
      setProdutosSelecionados(new Set());
      setProdutosQuantidades({});
      setProdutosBusca('');
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao adicionar produtos.'));
    } finally {
      setAdicionandoVarios(false);
    }
  };

  const selecionarProdutoSugerido = (nome, option) => {
    setProdutoOptions([]);
    // Já traz a unidade com que o produto costuma ser comprado (kg, un, L...) — do
    // histórico de preço ou, se nunca foi comprado, das listas anteriores.
    const chave = nome.trim().toLowerCase();
    const unidade = option?.unidade
      || produtosFrequentes.find((p) => (p.nome || '').trim().toLowerCase() === chave)?.unidade;
    if (unidade) {
      const qtd = addForm.getFieldValue('quantidade');
      addForm.setFieldsValue({
        unidade,
        ...(!UNIDADES_FRACIONARIAS.has(unidade) && qtd != null && !Number.isInteger(qtd) ? { quantidade: 1 } : {}),
      });
    }
    if (!addGtin) sugerirPreco({ nome });
  };

  const ditarProduto = () => {
    if (!vozDisponivel) return;
    if (ouvindo) {
      recognitionRef.current?.stop();
      return;
    }
    const rec = new SpeechRecognition();
    rec.lang = 'pt-BR';
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const texto = e.results?.[0]?.[0]?.transcript?.trim();
      if (texto) {
        addForm.setFieldsValue({ nome_produto: texto });
        setAddGtin('');
        sugerirPreco({ nome: texto });
      }
    };
    rec.onerror = () => setOuvindo(false);
    rec.onend = () => setOuvindo(false);
    recognitionRef.current = rec;
    setOuvindo(true);
    rec.start();
  };

  const abrirScanner = (alvo) => {
    setScannerAlvo(alvo);
    setScannerOpen(true);
  };

  // Consulta o GTIN no catálogo. Sem match, pede o nome ao usuário (vira contribuição
  // do catálogo colaborativo). Resolve com { nome, marca, conteudo } ou null.
  // `nomeSugerido` pré-preenche o campo (na substituição: o nome do item genérico).
  const resolverGtin = async (gtin, nomeSugerido = '') => {
    const p = await getProdutoPorGtin(gtin);
    if (p.found) return { nome: p.nome, marca: p.marca || '', conteudo: p.conteudo || '' };
    return new Promise((resolve) => {
      let nome = nomeSugerido;
      let marca = '';
      modal.confirm({
        title: 'Produto não reconhecido',
        content: (
          <div>
            <Text type="secondary">
              Código {gtin}. Dê um nome — fica salvo para todos os usuários.
            </Text>
            <Input
              style={{ marginTop: 12 }}
              placeholder="Nome do produto"
              defaultValue={nomeSugerido}
              onChange={(e) => { nome = e.target.value; }}
            />
            <Input
              style={{ marginTop: 8 }}
              placeholder="Marca (opcional)"
              maxLength={80}
              onChange={(e) => { marca = e.target.value; }}
            />
          </div>
        ),
        okText: 'Salvar',
        onOk: async () => {
          if (!nome.trim()) { resolve(null); return; }
          try {
            await contribuirProdutoCatalogo({ gtin, nome: nome.trim(), marca: marca.trim() });
          } catch { /* segue mesmo assim */ }
          resolve({ nome: nome.trim(), marca: marca.trim(), conteudo: '' });
        },
        onCancel: () => resolve(null),
      });
    });
  };

  const aoEscanear = async (gtin) => {
    setScannerOpen(false);
    try {
      if (scannerAlvo === 'substituir' && substItem) {
        const p = await resolverGtin(gtin, substItem.nome_produto);
        if (!p) return;
        setSubstGtin(gtin);
        substForm.setFieldsValue({ nome_produto: p.nome, marca: p.marca, conteudo: p.conteudo });
        return;
      }
      const p = await resolverGtin(gtin);
      if (!p) return;
      setAddGtin(gtin);
      if (p.marca || p.conteudo) setAddExtrasAberto(true);
      addForm.setFieldsValue({
        nome_produto: p.nome,
        marca: p.marca || undefined,
        conteudo: p.conteudo || undefined,
      });
      await sugerirPreco({ gtin });
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao consultar o código de barras.'));
    }
  };

  const abrirSubstituir = (item) => {
    setSubstItem(item);
    setSubstGtin('');
    substForm.setFieldsValue({ nome_produto: '', marca: '', conteudo: '', preco_pago: null });
  };

  const salvarSubstituicao = async (values) => {
    setSaving(true);
    try {
      const preco = values.preco_pago ?? null;
      await substituirItem(substItem, {
        nome_produto: values.nome_produto,
        marca: values.marca,
        conteudo: values.conteudo,
        gtin: substGtin,
        extra: modoCompra && preco != null && preco > 0
          ? { preco_unitario: preco, comprado: true }
          : {},
      });
      setSubstItem(null);
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao substituir item.'));
    } finally {
      setSaving(false);
    }
  };

  const abrirEditarItem = (item) => {
    setEditItem(item);
    editForm.setFieldsValue({
      nome_produto: item.nome_produto,
      marca: item.marca || '',
      conteudo: item.conteudo || '',
      quantidade: Number(item.quantidade),
      unidade: item.unidade,
      preco_estimado: item.preco_estimado != null ? Number(item.preco_estimado) : null,
      secao: item.secao ?? undefined,
      observacao: item.observacao || '',
      link_produto: item.link_produto || '',
      prioridade: item.prioridade || undefined,
      disponivel_em: item.disponivel_em ? dayjs(String(item.disponivel_em).slice(0, 10)) : null,
    });
  };

  const salvarEdicaoItem = async (values) => {
    setSaving(true);
    try {
      const payload = {
        nome_produto: values.nome_produto.trim(),
        marca: (values.marca || '').trim(),
        conteudo: (values.conteudo || '').trim(),
        quantidade: values.quantidade ?? 1,
        unidade: values.unidade,
        preco_estimado: values.preco_estimado ?? null,
        observacao: values.observacao?.trim() || '',
      };
      // Só envia `secao` quando o usuário mexeu nela — enviar sempre gravaria uma
      // "preferência manual" de setor que ele não escolheu de propósito.
      if ((values.secao ?? null) !== (editItem.secao ?? null)) {
        payload.secao = values.secao ?? null;
      }
      if (ehMarketplace) {
        payload.link_produto = (values.link_produto || '').trim();
        payload.prioridade = values.prioridade || '';
        payload.disponivel_em = values.disponivel_em ? values.disponivel_em.format('YYYY-MM-DD') : null;
      }
      await updateItemLista(editItem.id, payload);
      setEditItem(null);
      const atualizada = await getListaCompra(id);
      setLista(atualizada);
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao salvar item.'));
    } finally {
      setSaving(false);
    }
  };

  const removerItem = (item) => {
    modal.confirm({
      title: `Remover "${item.nome_produto}"?`,
      okText: 'Remover',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteItemLista(item.id);
          const atualizada = await getListaCompra(id);
          setLista(atualizada);
        } catch (err) {
          message.error(getErrorMessage(err, 'Erro ao remover item.'));
        }
      },
    });
  };

  // --- Setores (corredores) ---
  const adicionarSecao = async (nome) => {
    const limpo = (nome || '').trim();
    if (!limpo) return;
    try {
      await createSecaoCompra(limpo);
      recarregarSecoes();
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao criar setor.'));
    }
  };

  const renomearSecao = async (secao, nome) => {
    const limpo = (nome || '').trim();
    if (!limpo || limpo === secao.nome) return;
    try {
      await updateSecaoCompra(secao.id, { nome: limpo });
      recarregarSecoes();
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao renomear setor.'));
      recarregarSecoes();
    }
  };

  const removerSecao = (secao) => {
    modal.confirm({
      title: `Remover o setor "${secao.nome}"?`,
      content: 'Os itens deste setor ficam sem setor — nada é apagado da lista.',
      okText: 'Remover',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteSecaoCompra(secao.id);
          recarregarSecoes();
          const atualizada = await getListaCompra(id);
          setLista(atualizada);
        } catch (err) {
          message.error(getErrorMessage(err, 'Erro ao remover setor.'));
        }
      },
    });
  };

  const moverSecao = async (idx, delta) => {
    const nova = [...secoes];
    const alvo = idx + delta;
    if (alvo < 0 || alvo >= nova.length) return;
    [nova[idx], nova[alvo]] = [nova[alvo], nova[idx]];
    setSecoes(nova); // otimista
    try {
      const salvas = await reordenarSecoesCompra(nova.map((s) => s.id));
      if (salvas.length) setSecoes(salvas);
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao reordenar.'));
      recarregarSecoes();
    }
  };

  const fecharSecoes = async () => {
    setSecoesOpen(false);
    const atualizada = await getListaCompra(id).catch(() => null);
    if (atualizada) setLista(atualizada);
  };

  // --- Ações da lista ---
  const abrirEditarMeta = () => {
    metaForm.setFieldsValue({
      titulo: lista.titulo || '',
      data_compra: lista.data_compra ? dayjs(String(lista.data_compra).slice(0, 10)) : null,
      observacao: lista.observacao || '',
    });
    setMetaOpen(true);
  };

  const salvarMeta = async (values) => {
    setSaving(true);
    try {
      const atualizada = await updateListaCompra(id, {
        titulo: values.titulo?.trim() || '',
        data_compra: values.data_compra ? values.data_compra.format('YYYY-MM-DD') : null,
        observacao: values.observacao?.trim() || '',
      });
      setMetaOpen(false);
      setLista(atualizada);
      message.success('Lista atualizada.');
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao salvar.'));
    } finally {
      setSaving(false);
    }
  };

  const duplicar = async () => {
    try {
      const nova = await duplicarListaCompra(id);
      message.success('Lista duplicada.');
      navigate(`/listas-compras/${nova.id}`);
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao duplicar.'));
    }
  };

  const concluirSemLancar = async () => {
    let preview = {};
    try {
      preview = await getPreviewLancamento(id);
    } catch { /* segue sem sugestões */ }

    let estab = '';
    const conteudo = preview.precisa_estabelecimento ? (
      <div>
        <Text type="secondary">
          Marca como concluída e guarda os preços no histórico. Nenhum lançamento é criado.
        </Text>
        <div style={{ marginTop: 12 }}>
          <Text strong>Em qual loja foi a compra?</Text>
          <AutoComplete
            style={{ width: '100%', marginTop: 4 }}
            placeholder="Ex.: Assaí Aterrado"
            onChange={(v) => { estab = v; }}
            options={(preview.estabelecimentos_sugeridos || []).map((s) => ({ value: s }))}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            {preview.local_definido
              ? `“${preview.local_nome}” é genérico — o comparador precisa da loja específica.`
              : 'Esta lista não tem local — o histórico de preço fica ligado à loja que você informar.'}
          </Text>
        </div>
      </div>
    ) : (
      'Marca como concluída e guarda os preços no histórico. Nenhum lançamento é criado no financeiro.'
    );

    modal.confirm({
      title: 'Concluir a compra?',
      content: conteudo,
      okText: 'Concluir',
      onOk: async () => {
        try {
          const atualizada = await concluirListaCompra(id, estab.trim());
          setLista(atualizada);
          message.success('Compra concluída. Preços registrados no histórico.');
          ofertarListaPendentes(atualizada);
        } catch (err) {
          message.error(getErrorMessage(err, 'Erro ao concluir.'));
        }
      },
    });
  };

  const salvarModelo = () => {
    let titulo = lista.titulo || 'Modelo';
    modal.confirm({
      title: 'Salvar como modelo',
      content: (
        <div>
          <Text type="secondary">Cria uma cópia reutilizável (sem preços), que aparece em “Um modelo” ao criar novas listas.</Text>
          <Input
            defaultValue={titulo}
            style={{ marginTop: 12 }}
            onChange={(e) => { titulo = e.target.value; }}
          />
        </div>
      ),
      okText: 'Salvar modelo',
      onOk: async () => {
        try {
          await salvarListaComoModelo(id, titulo);
          message.success('Modelo salvo.');
        } catch (err) {
          message.error(getErrorMessage(err, 'Erro ao salvar modelo.'));
        }
      },
    });
  };

  const excluirLista = () => {
    modal.confirm({
      title: 'Excluir esta lista?',
      content: lista.transacao
        ? 'O lançamento já criado no financeiro NÃO será excluído.'
        : 'Esta ação não pode ser desfeita.',
      okText: 'Excluir',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteListaCompra(id);
          message.success('Lista excluída.');
          navigate('/listas-compras');
        } catch (err) {
          message.error(getErrorMessage(err, 'Erro ao excluir.'));
        }
      },
    });
  };

  // Itens que foram pegos mas cujo valor só sai no caixa — a fila da conferência do cupom.
  const itensAguardando = useMemo(
    () => itens.filter((i) => i.comprado && i.preco_unitario == null),
    [itens],
  );

  // Grava o que o cupom mostra. Com o peso preenchido, o item vira kg e o preço por kg
  // sai exato — é a única forma de o histórico de R$/kg aprender alguma coisa em
  // hortifrúti, já que derivar o peso do próprio histórico daria sempre o número velho.
  const salvarCupom = async () => {
    const alvos = itensAguardando
      .map((item) => ({ item, dados: cupomValores[item.id] || {} }))
      .filter(({ dados }) => dados.total != null && dados.total > 0);
    if (alvos.length === 0) {
      setCupomOpen(false);
      return;
    }
    setCupomSalvando(true);
    try {
      await Promise.all(alvos.map(({ item, dados }) => {
        const peso = dados.peso != null && dados.peso > 0 ? Number(dados.peso) : null;
        const patch = peso
          ? { unidade: 'KG', quantidade: peso, preco_unitario: round2(dados.total / peso) }
          : { preco_unitario: round2(dados.total / (Number(item.quantidade) || 1)) };
        return updateItemLista(item.id, patch);
      }));
      const atualizada = await getListaCompra(id);
      setLista(atualizada);
      setCupomValores({});
      setCupomOpen(false);
      message.success(`${alvos.length} ${alvos.length === 1 ? 'valor preenchido' : 'valores preenchidos'}.`);
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao salvar os valores do cupom.'));
    } finally {
      setCupomSalvando(false);
    }
  };

  const abrirLancar = async () => {
    // Fechar a compra com item sem valor significa lançar R$ 0,00 por ele no financeiro —
    // o usuário decide, mas sabendo.
    if (resumo.aguardandoValor > 0 && !cupomOpen) {
      modal.confirm({
        title: `${resumo.aguardandoValor} ${resumo.aguardandoValor === 1 ? 'item ainda sem valor' : 'itens ainda sem valor'}`,
        content: 'Eles entram como R$ 0,00 no lançamento. Quer preencher pelo cupom antes de finalizar?',
        okText: 'Conferir cupom',
        cancelText: 'Lançar assim mesmo',
        keyboard: false,
        onOk: () => setCupomOpen(true),
        onCancel: () => lancarDireto(),
      });
      return;
    }
    lancarDireto();
  };

  const lancarDireto = async () => {
    try {
      const preview = await getPreviewLancamento(id);
      setFaturas([]);
      setLocalGenerico(
        preview.precisa_estabelecimento
          ? {
              nome: preview.local_nome,
              definido: preview.local_definido,
              sugestoes: preview.estabelecimentos_sugeridos || [],
            }
          : null,
      );
      lancarForm.setFieldsValue({
        descricao: preview.descricao,
        valor: Number(preview.valor) || 0,
        categoria: preview.categoria || undefined,
        forma_pagamento: 'conta',
        conta: undefined,
        cartao: undefined,
        fatura: undefined,
        estabelecimento: '',
        data: dayjs(), // hoje — o usuário conclui quando termina a compra
        status: preview.status || 'PAGO',
        parcelas: 1,
      });
      setLancarOpen(true);
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao preparar lançamento.'));
    }
  };

  const confirmarLancar = async (values) => {
    setSaving(true);
    try {
      const payload = {
        descricao: values.descricao.trim(),
        valor: values.valor,
        categoria: values.categoria,
        data: values.data.format('YYYY-MM-DD'),
        status: values.status,
        parcelas: values.parcelas || 1,
        estabelecimento: (values.estabelecimento || '').trim(),
      };
      if (values.forma_pagamento === 'cartao') {
        payload.cartao = values.cartao;
        payload.fatura = values.fatura;
      } else {
        payload.conta = values.conta;
      }

      const res = await lancarListaCompra(id, payload);
      setLancarOpen(false);
      message.success('Compra lançada no financeiro.');
      setLista(res.lista);
      ofertarListaPendentes(res.lista);
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao lançar compra.'));
    } finally {
      setSaving(false);
    }
  };

  // Factory: cada modal de lançamento (lista inteira / item individual) tem seu próprio
  // Form + estado de faturas, mas a lógica de buscar as faturas do cartão é a mesma.
  const carregarFaturasEm = (form, setFaturasFn, setLoadingFn) => async (cartaoId) => {
    form.setFieldsValue({ fatura: undefined });
    if (!cartaoId) { setFaturasFn([]); return; }
    setLoadingFn(true);
    try {
      const rows = await getFaturasDisponiveis(cartaoId);
      setFaturasFn(rows);
      const aberta = rows.find((f) => f.status === 'ABERTA');
      form.setFieldsValue({ fatura: (aberta || rows[0])?.id });
    } catch (err) {
      setFaturasFn([]);
      message.error(getErrorMessage(err, 'Erro ao carregar faturas.'));
    } finally {
      setLoadingFn(false);
    }
  };

  const carregarFaturas = carregarFaturasEm(lancarForm, setFaturas, setFaturasLoading);
  const carregarFaturasItem = carregarFaturasEm(itemLancarForm, setItemFaturas, setItemFaturasLoading);

  // --- Lançamento individual de item (wishlist de marketplace) ---
  const abrirLancarItem = async (item, ignorarReflexao) => {
    try {
      const preview = await getPreviewLancamentoItem(item.id);
      setItemFaturas([]);
      setIgnorarReflexaoAtual(ignorarReflexao);
      itemLancarForm.setFieldsValue({
        descricao: preview.descricao,
        valor: Number(preview.valor) || 0,
        categoria: preview.categoria || undefined,
        forma_pagamento: 'conta',
        conta: undefined,
        cartao: undefined,
        fatura: undefined,
        data: dayjs(),
        status: preview.status || 'PAGO',
        parcelas: 1,
      });
      setItemLancarAlvo(item);
      setItemLancarOpen(true);
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao preparar lançamento do item.'));
    }
  };

  // Item ainda "em reflexão" (anti-impulso) → confirma antes de abrir o lançamento.
  const iniciarLancarItem = (item) => {
    if (emReflexao(item)) {
      modal.confirm({
        title: 'Pausa anti-impulso ativa',
        content: `Você pediu pra esperar até ${fmtData(item.disponivel_em)} antes de comprar "${item.nome_produto}" — dá tempo de pensar se vale a pena. Comprar mesmo assim?`,
        okText: 'Comprar mesmo assim',
        cancelText: 'Esperar',
        onOk: () => abrirLancarItem(item, true),
      });
    } else {
      abrirLancarItem(item, false);
    }
  };

  const confirmarLancarItem = async (values) => {
    if (!itemLancarAlvo) return;
    setSaving(true);
    try {
      const payload = {
        descricao: values.descricao.trim(),
        valor: values.valor,
        categoria: values.categoria,
        data: values.data.format('YYYY-MM-DD'),
        status: values.status,
        parcelas: values.parcelas || 1,
        ignorar_reflexao: ignorarReflexaoAtual,
      };
      if (values.forma_pagamento === 'cartao') {
        payload.cartao = values.cartao;
        payload.fatura = values.fatura;
      } else {
        payload.conta = values.conta;
      }
      await lancarItemApi(itemLancarAlvo.id, payload);
      setItemLancarOpen(false);
      setItemLancarAlvo(null);
      message.success('Item lançado no financeiro.');
      const atualizada = await getListaCompra(id);
      setLista(atualizada);
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao lançar item.'));
    } finally {
      setSaving(false);
    }
  };

  // No Modo Compra o preço deixou de ser opcional: confirmar que pegou um item sem
  // dizer quanto custou colocava R$ 0,00 no total e quebrava a comparação com o
  // previsto. O toque agora leva pro campo de preço, e é o preço confirmado que marca
  // o item como pego — um gesto só, sem estado intermediário inválido.
  const alternarComprado = (item, checked) => {
    if (checked && modoCompra && item.preco_unitario == null) {
      setFocoPrecoId(item.id);
      return;
    }
    if (checked && emReflexao(item)) {
      modal.confirm({
        title: 'Pausa anti-impulso ativa',
        content: `Você pediu pra esperar até ${fmtData(item.disponivel_em)} antes de comprar "${item.nome_produto}" — dá tempo de pensar se vale a pena. Marcar como comprado mesmo assim?`,
        okText: 'Comprar mesmo assim',
        cancelText: 'Esperar',
        onOk: () => { salvarItemCampo(item, { comprado: true }); navigator.vibrate?.(12); },
      });
    } else {
      salvarItemCampo(item, { comprado: checked });
      navigator.vibrate?.(12);
    }
  };

  // Preço confirmado na linha. No Modo Compra ele é o que define se o item está no
  // carrinho: com preço entra, sem preço volta a pendente. Assim "pego" e "tem preço"
  // nunca divergem — não existe mais item pego valendo R$ 0,00.
  const precoConfirmado = (item, valor) => {
    const patch = { preco_unitario: valor };
    const temPreco = valor != null && valor > 0;
    if (modoCompra && temPreco && !item.comprado && !emReflexao(item)) patch.comprado = true;
    salvarItemCampo(item, patch);
    if (patch.comprado) navigator.vibrate?.(12);
  };

  // "Peguei, mas o valor só sai no caixa" — a saída pro hortifrúti, que quase ninguém
  // pesa na balança do corredor. É uma escolha explícita e rotulada: o item conta como
  // pego, aparece com selo, e a lista cobra o valor antes de finalizar. O que continua
  // proibido é o R$ 0,00 silencioso, que era o problema original.
  const confirmarSemValor = (item) => {
    salvarItemCampo(item, { comprado: true, preco_unitario: null });
    setFocoPrecoId((cur) => (cur === item.id ? null : cur));
    navigator.vibrate?.(12);
  };

  // --- Verificação manual de preço (link do marketplace) ---
  const abrirVerificarPreco = (item) => {
    verificarForm.setFieldsValue({
      preco: item.preco_estimado != null ? Number(item.preco_estimado) : null,
      data: dayjs(),
    });
    setVerificarItem(item);
  };

  const confirmarVerificarPreco = async (values) => {
    if (!verificarItem) return;
    setSaving(true);
    try {
      await verificarPrecoItem(verificarItem.id, {
        preco: values.preco,
        data: values.data.format('YYYY-MM-DD'),
      });
      message.success('Preço registrado.');
      setVerificarItem(null);
      const atualizada = await getListaCompra(id);
      setLista(atualizada);
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao registrar preço.'));
    } finally {
      setSaving(false);
    }
  };

  const unidadeAddWatch = Form.useWatch('unidade', addForm);
  const precoAddWatch = Form.useWatch('preco_estimado', addForm);
  const unidadeEditWatch = Form.useWatch('unidade', editForm);
  const disponivelAddWatch = Form.useWatch('disponivel_em', addForm);
  const disponivelEditWatch = Form.useWatch('disponivel_em', editForm);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center' }}>
        <Spin size="large" tip="Carregando..." />
      </div>
    );
  }
  if (error || !lista) {
    return (
      <Content style={{ padding: '2rem' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/listas-compras')}>Voltar</Button>
        <Alert type="error" showIcon message={error || 'Lista não encontrada.'} style={{ marginTop: 16 }} />
      </Content>
    );
  }

  const st = STATUS_TAG[lista.status] || STATUS_TAG.RASCUNHO;

  const painelTotais = (
    <Card style={{ marginBottom: 16, background: '#F5F0E6' }} styles={{ body: { padding: modoCompra ? 14 : 20 } }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 130px', maxWidth: 240, minWidth: 0 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{ehMarketplace ? 'NA LISTA' : 'PREVISTO'}</Text>
          <div style={{ fontSize: modoCompra ? 24 : 22, fontWeight: 700, color: '#1F4F4F' }}>
            {brl(lista.total_estimado)}
          </div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {ehMarketplace ? `${resumo.total} ${resumo.total === 1 ? 'item desejado' : 'itens desejados'}` : 'plano da lista'}
          </Text>
        </div>
        <div style={{ flex: '1 1 130px', maxWidth: 240, minWidth: 0 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{ehMarketplace ? 'JÁ GASTO' : 'CONFIRMADO'}</Text>
          <div style={{ fontSize: modoCompra ? 24 : 22, fontWeight: 700, color: '#2F9E6F' }}>
            {brl(ehMarketplace ? resumoMarketplace.jaGasto : lista.total_final)}
          </div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {ehMarketplace
              ? `${resumoMarketplace.lancados} ${resumoMarketplace.lancados === 1 ? 'item lançado' : 'itens lançados'}`
              : `${resumo.comprados} de ${resumo.total} ${resumo.total === 1 ? 'item' : 'itens'}`}
          </Text>
        </div>
      </div>
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
        {ehMarketplace ? (
          <Text type="secondary">
            {resumo.total} {resumo.total === 1 ? 'item' : 'itens'} · {resumo.pendentes} ainda por comprar
          </Text>
        ) : resumo.difComprados != null ? (
          <Text
            strong
            style={{
              color:
                Math.abs(resumo.difComprados) < 0.01
                  ? '#8c8c8c'
                  : resumo.difComprados < 0
                  ? '#2F9E6F'
                  : '#D64545',
            }}
          >
            {Math.abs(resumo.difComprados) < 0.01
              ? `Nos ${resumo.comprados} ${resumo.comprados === 1 ? 'item pego' : 'itens pegos'}: no previsto`
              : resumo.difComprados < 0
              ? `Nos ${resumo.comprados} ${resumo.comprados === 1 ? 'item pego' : 'itens pegos'}: ${brl(-resumo.difComprados)} abaixo do previsto`
              : `Nos ${resumo.comprados} ${resumo.comprados === 1 ? 'item pego' : 'itens pegos'}: ${brl(resumo.difComprados)} acima do previsto`}
          </Text>
        ) : (
          <Text type="secondary">
            {resumo.total} {resumo.total === 1 ? 'item' : 'itens'} · {resumo.pendentes} pendente(s)
          </Text>
        )}
      </div>
      {modoCompra && (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Confere o caixa:</Text>
          <InputNumber
            {...MONEY}
            size="small"
            min={0}
            placeholder="total cobrado"
            value={totalCaixa}
            onChange={setTotalCaixa}
            style={{ width: 130 }}
          />
          {totalCaixa != null && (() => {
            const dif = Number(totalCaixa) - Number(lista.total_final);
            if (Math.abs(dif) < 0.01) return <Text style={{ color: '#2F9E6F' }}>bate ✓</Text>;
            return (
              <Text style={{ color: '#D64545', fontSize: 13 }}>
                {dif > 0 ? '+' : '−'}{brl(Math.abs(dif))} {dif > 0 ? '(a mais)' : '(a menos)'}
              </Text>
            );
          })()}
        </div>
      )}
      {!modoCompra && !ehMarketplace && (
        <div style={{ marginTop: 12 }}>
          <Button
            type="link"
            size="small"
            icon={<SwapOutlined />}
            style={{
              paddingLeft: 0, height: 'auto', whiteSpace: 'normal',
              textAlign: 'left', lineHeight: 1.3,
            }}
            onClick={() => navigate('/simuladores/comparar-precos')}
          >
            Comparar preços (qual sai mais barato por kg/un)
          </Button>
        </div>
      )}
    </Card>
  );

  const formAdicionar = (
    <>
    {produtosDisponiveis.length > 0 && (
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Produtos frequentes — toque pra adicionar</Text>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto', fontSize: 12 }}
            onClick={() => setProdutosModalOpen(true)}
          >
            Ver todos ({produtosDisponiveis.length})
          </Button>
        </div>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '6px 2px', marginTop: 2 }}>
          {sugestoesFrequentes.map((p) => (
            <Tag
              key={p.chave}
              onClick={() => adicionarProdutoFrequente(p)}
              style={{ cursor: 'pointer', flex: '0 0 auto', padding: '4px 10px', fontSize: 13 }}
            >
              {p.nome} <Text type="secondary" style={{ fontSize: 11 }}>· {p.vezes}x</Text>
            </Tag>
          ))}
        </div>
      </div>
    )}
    {!ehMarketplace && (
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <Button
          size="large"
          icon={<BarcodeOutlined />}
          onClick={() => abrirScanner('add')}
          style={{ flex: '1 1 130px' }}
        >
          Escanear
        </Button>
        {vozDisponivel && (
          <Button
            size="large"
            icon={<AudioOutlined />}
            type={ouvindo ? 'primary' : 'default'}
            danger={ouvindo}
            onClick={ditarProduto}
            style={{ flex: '1 1 130px' }}
          >
            {ouvindo ? 'Ouvindo…' : 'Ditar nome'}
          </Button>
        )}
      </div>
    )}
    <Form form={addForm} onFinish={adicionarItem} initialValues={{ quantidade: 1, unidade: 'UN' }}>
      <Form.Item name="nome_produto" rules={[{ required: true, message: 'Informe o produto' }]} style={{ marginBottom: 8 }}>
        <AutoComplete
          options={produtoOptions}
          onSearch={setNomeBusca}
          onSelect={selecionarProdutoSugerido}
          filterOption={false}
        >
          <Input
            ref={addNomeRef}
            placeholder="Nome do produto..."
            size="large"
            maxLength={160}
            onBlur={(e) => {
              const nome = e.target.value.trim();
              if (nome && !addGtin) sugerirPreco({ nome });
            }}
            onChange={() => { if (addGtin) setAddGtin(''); }}
          />
        </AutoComplete>
      </Form.Item>
      <Row gutter={8}>
        <Col flex="88px">
          <Form.Item name="quantidade" style={{ marginBottom: 8 }}>
            <InputNumber {...qtdInputProps(unidadeAddWatch)} style={{ width: '100%' }} placeholder="Qtd" />
          </Form.Item>
        </Col>
        <Col flex="84px">
          <Form.Item name="unidade" style={{ marginBottom: 8 }}>
            <Select options={UNIDADES} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col flex="auto">
          <Form.Item
            name="preco_estimado"
            style={{ marginBottom: 8 }}
            tooltip={
              modoCompra
                ? 'Como você está no Modo Compra, este é o preço pago — o item entra já marcado como comprado.'
                : 'Preenchido pelo histórico. Deixe em branco se não tiver ideia.'
            }
          >
            <InputNumber
              {...MONEY}
              min={0}
              placeholder={modoCompra ? 'preço pago' : 'est. (auto)'}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </Row>
      {addExtrasAberto ? (
        <Row gutter={8}>
          <Col span={12}>
            <Form.Item name="marca" style={{ marginBottom: 8 }}>
              <Input placeholder="Marca (opcional)" maxLength={80} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="conteudo" style={{ marginBottom: 8 }}>
              <Input placeholder="Tamanho (ex.: 500g)" maxLength={40} />
            </Form.Item>
          </Col>
        </Row>
      ) : (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, marginBottom: 8 }}
          onClick={() => setAddExtrasAberto(true)}
        >
          + Marca / tamanho da embalagem
        </Button>
      )}
      {ehMarketplace && (
        <>
          <Form.Item name="link_produto" style={{ marginBottom: 8 }}>
            <Input placeholder="Link do produto (opcional)" maxLength={500} prefix={<LinkOutlined />} />
          </Form.Item>
          <Form.Item
            name="prioridade"
            label="Prioridade"
            tooltip="Itens de prioridade alta aparecem primeiro na lista — ajuda a decidir o que comprar primeiro quando o orçamento é curto."
            style={{ marginBottom: 8 }}
          >
            <Select allowClear placeholder="Sem prioridade" options={PRIORIDADES} />
          </Form.Item>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
            <Text style={{ fontSize: 13 }}>Pausa anti-impulso</Text>
            <Tooltip title="Até essa data, o app confirma com você antes de marcar como comprado ou lançar o item — um tempo pra pensar se vale a pena. Você pode comprar antes se quiser, só vai confirmar.">
              <QuestionCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
            </Tooltip>
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
            {[3, 7, 15, 30].map((n) => {
              const ativo = disponivelAddWatch && dayjs(disponivelAddWatch).isSame(dayjs().add(n, 'day'), 'day');
              return (
                <Button
                  key={n}
                  size="small"
                  type={ativo ? 'primary' : 'default'}
                  onClick={() => addForm.setFieldsValue({ disponivel_em: dayjs().add(n, 'day') })}
                >
                  +{n}d
                </Button>
              );
            })}
            {disponivelAddWatch && (
              <Button size="small" type="text" onClick={() => addForm.setFieldsValue({ disponivel_em: null })}>
                limpar
              </Button>
            )}
          </div>
          <Form.Item name="disponivel_em" style={{ marginBottom: 8 }}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="ou escolha outra data" />
          </Form.Item>
        </>
      )}
      {/* Sem preço o item entra como pendente, não no carrinho — o rótulo diz qual dos
          dois vai acontecer, em vez de prometer "carrinho" e marcar outra coisa. */}
      <Button
        type="primary"
        htmlType="submit"
        icon={modoCompra && precoAddWatch != null ? <CheckCircleOutlined /> : <PlusOutlined />}
        block
        size="large"
      >
        {modoCompra
          ? (precoAddWatch != null ? 'Adicionar ao carrinho' : 'Adicionar como pendente')
          : 'Adicionar'}
      </Button>
    </Form>
    {addGtin && (
      <Text type="secondary" style={{ fontSize: 11 }}>Código de barras: {addGtin}</Text>
    )}
    {modoCompra && (
      <Button block size="large" style={{ marginTop: 8 }} onClick={() => setAddAberto(false)}>
        Voltar à lista
      </Button>
    )}
    </>
  );

  return (
    <Content
      style={{
        padding: modoCompra ? '0.75rem' : 'clamp(1rem, 4vw, 2rem)',
        paddingBottom: modoCompra && isMobile ? 96 : modoCompra ? '0.75rem' : undefined,
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
      }}
    >
      {modalCtx}
      <style>{`@keyframes itemNovoFlash { 0% { background: #FBEFCF; } 70% { background: #FBEFCF; } 100% { background: transparent; } }`}</style>

      {modoCompra ? (
        /* Cabeçalho do Modo Compra: uma faixa fixa de ~2 linhas no lugar do painel de
           números de 490px que empurrava a lista inteira pra fora da tela. O valor do
           carrinho abre o painel completo (previsto, diferença, conferir caixa) — coisas
           que só importam no fim da compra, não a cada item pego. */
        <div
          style={{
            position: 'sticky', top: 0, zIndex: 20, margin: '-0.75rem -0.75rem 10px',
            padding: '10px 12px 8px', background: '#fff',
            borderBottom: '1px solid #EADFC8',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShoppingCartOutlined style={{ fontSize: 18, color: '#B8860B', flex: '0 0 auto' }} />
            <Text strong style={{ fontSize: 15, flex: '1 1 auto', minWidth: 0 }} ellipsis={{ tooltip: lista.titulo }}>
              {lista.titulo || 'Lista'}
            </Text>
            <Text strong style={{ fontSize: 13, color: '#1F4F4F', flex: '0 0 auto' }}>
              {resumo.comprados}/{resumo.total}
            </Text>
            <Button
              size="small"
              style={{ flex: '0 0 auto' }}
              onClick={() => { setModoCompra(false); setAddAberto(false); }}
            >
              Sair
            </Button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
            <div style={{ flex: '1 1 auto', height: 6, borderRadius: 3, background: '#EFE8D8', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  width: `${resumo.total ? Math.round((resumo.comprados / resumo.total) * 100) : 0}%`,
                  background: '#2F9E6F',
                  borderRadius: 3,
                  transition: 'width 0.25s ease',
                }}
              />
            </div>
            <Button
              type="text"
              size="small"
              onClick={() => setPainelOpen(true)}
              style={{ flex: '0 0 auto', height: 'auto', padding: '0 4px' }}
            >
              <Text strong style={{ fontSize: 16, color: '#2F9E6F' }}>{brl(lista.total_final)}</Text>
              <DownOutlined style={{ fontSize: 9, marginLeft: 4, color: '#8c8c8c' }} />
            </Button>
          </div>
          {/* Chips de estado dentro da faixa fixa: "o que ainda falta?" é a pergunta
              mais repetida do corredor e não pode exigir rolar a tela de volta. */}
          {itens.length > 0 && !buscaAtiva && (
            <div style={{ marginTop: 8 }}>
              <Segmented
                block
                value={filtro}
                onChange={setFiltro}
                options={[
                  { value: 'PENDENTES', label: `A pegar ${resumo.pendentes - resumo.naoEncontrados}` },
                  { value: 'PEGOS', label: `Pegos ${resumo.comprados}` },
                  ...(resumo.naoEncontrados > 0
                    ? [{ value: 'FORA', label: `Não achei ${resumo.naoEncontrados}` }]
                    : []),
                  { value: 'TODOS', label: 'Tudo' },
                ]}
              />
            </div>
          )}
          {resumo.aguardandoValor > 0 && (
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <Text style={{ fontSize: 12, color: '#B8860B' }}>
                {resumo.aguardandoValor === 1
                  ? '1 item aguardando o valor do cupom'
                  : `${resumo.aguardandoValor} itens aguardando o valor do cupom`}
              </Text>
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto', fontSize: 12 }}
                onClick={() => setCupomOpen(true)}
              >
                conferir agora
              </Button>
            </div>
          )}
        </div>
      ) : (
        <>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/listas-compras')} style={{ paddingLeft: 0, marginBottom: 8 }}>
            Listas de compras
          </Button>

          <Row align="middle" gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={15}>
              <Space align="center" wrap>
                <Title level={3} style={{ margin: 0 }}>{lista.titulo || 'Lista'}</Title>
                <Tooltip title="Editar nome, data e observação">
                  <Button type="text" size="small" icon={<EditOutlined />} onClick={abrirEditarMeta} />
                </Tooltip>
                <Tag color={st.color}>{st.label}</Tag>
                {lista.eh_modelo && <Tag color="purple">Modelo</Tag>}
              </Space>
              <div>
                {lista.local_nome ? (
                  <Text type="secondary">{lista.local_nome} · {fmtData(lista.data_compra)}</Text>
                ) : (
                  <Space size={4}>
                    <Text type="secondary">Sem local · {fmtData(lista.data_compra)}</Text>
                    {!lista.eh_modelo && !bloqueada && (
                      <Select
                        size="small"
                        variant="borderless"
                        placeholder="definir local"
                        style={{ minWidth: 130 }}
                        options={locais.map((l) => ({ value: l.id, label: l.nome }))}
                        onChange={definirLocal}
                      />
                    )}
                  </Space>
                )}
              </div>
              {lista.observacao && (
                <div><Text type="secondary" style={{ fontSize: 12 }}>{lista.observacao}</Text></div>
              )}
            </Col>
            {!lista.eh_modelo && !bloqueada && !ehMarketplace && (
              <Col xs={24} md={9}>
                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<ShoppingCartOutlined />}
                  onClick={() => { setModoCompra(true); setAddAberto(false); }}
                >
                  Iniciar modo compra
                </Button>
              </Col>
            )}
          </Row>
        </>
      )}

      {alertaOrc && !modoCompra && (
        <Alert
          style={{ marginBottom: 16 }}
          type={alertaOrc.vai_estourar ? 'warning' : 'info'}
          showIcon
          message={
            alertaOrc.vai_estourar
              ? `Orçamento de ${alertaOrc.categoria}: restam ${brl(alertaOrc.restante)} este mês. Esta lista (${brl(alertaOrc.previsto_lista)}) ultrapassa em ${brl(alertaOrc.estouro)}.`
              : `Orçamento de ${alertaOrc.categoria}: ${brl(alertaOrc.gasto_mes)} de ${brl(alertaOrc.meta)} usados. Esta lista prevê ${brl(alertaOrc.previsto_lista)} (restam ${brl(alertaOrc.restante)}).`
          }
        />
      )}

      {sugestaoMercado && !sugestaoFechada && !modoCompra && (
        <Alert
          style={{ marginBottom: 16 }}
          type="success"
          showIcon
          closable
          onClose={() => setSugestaoFechada(true)}
          message={`Esta lista tende a sair mais barata no ${sugestaoMercado.local}`}
          description={`Cerca de ${brl(sugestaoMercado.economia)} a menos que no ${sugestaoMercado.segundo}, pelo que você já pagou nesses itens.`}
          action={(
            <Space direction="vertical" size={4}>
              <Button size="small" type="primary" onClick={() => definirLocal(sugestaoMercado.local_id)}>
                Usar esta loja
              </Button>
              <Button size="small" onClick={abrirComparador}>Ver comparação</Button>
            </Space>
          )}
        />
      )}

      {/* Painel de números. Fora do Modo Compra fica inline (é o resumo da lista); dentro
          dele vira bottom sheet, aberto pelo valor do carrinho no cabeçalho — previsto,
          diferença e conferência de caixa só importam no fim, não a cada item pego. */}
      {modoCompra ? (
        <Drawer
          placement="bottom"
          height="auto"
          open={painelOpen}
          onClose={() => setPainelOpen(false)}
          title="Números da compra"
          styles={{ body: { padding: 12 } }}
        >
          {painelTotais}
        </Drawer>
      ) : painelTotais}

      {/* Adicionar produto. Fora do Modo Compra é um cartão no fluxo (montar a lista é a
          tarefa principal ali). Dentro do Modo Compra vira bottom sheet acionado por um
          FAB: como formulário fixo, ele ocupava ~700px de altura e empurrava a lista
          inteira pra fora da tela justamente quando achar o próximo item é o que importa. */}
      {!bloqueada && !lista.eh_modelo && (
        modoCompra ? (
          <>
            {isMobile ? (
              /* No lugar do FAB global de lançamento financeiro, que fica escondido
                 durante o Modo Compra (ver efeito da classe sa-modo-compra). */
              <FloatButton
                icon={<PlusOutlined />}
                type="primary"
                tooltip="Adicionar item esquecido"
                style={{
                  right: 20,
                  bottom: 'calc(86px + var(--mobile-bottom-nav-height, 56px) + env(safe-area-inset-bottom, 0px))',
                  zIndex: 120,
                }}
                onClick={() => setAddAberto(true)}
              />
            ) : (
              <Button block icon={<PlusOutlined />} style={{ marginBottom: 16 }} onClick={() => setAddAberto(true)}>
                Adicionar item esquecido
              </Button>
            )}
            <Drawer
              placement="bottom"
              height="auto"
              open={addAberto}
              onClose={() => setAddAberto(false)}
              title="Adicionar item esquecido"
              styles={{ body: { padding: 12, maxHeight: '80vh', overflowY: 'auto' } }}
            >
              {formAdicionar}
            </Drawer>
          </>
        ) : (
          <Card size="small" style={{ marginBottom: 16 }}>{formAdicionar}</Card>
        )
      )}

      {/* Conferência do cupom — o hortifrúti é pesado no caixa, então o valor de cada
          item só existe depois que a compra termina. Esta é a fila desses itens: um
          campo de valor cada, com o cupom na mão. O peso é opcional e é o que faz o
          histórico de R$/kg aprender de fato. */}
      <Drawer
        placement="bottom"
        height="auto"
        open={cupomOpen}
        onClose={() => setCupomOpen(false)}
        title="Conferir cupom"
        styles={{ body: { padding: 12, maxHeight: '80vh', overflowY: 'auto' } }}
        footer={
          <div style={{ display: 'flex', gap: 8, padding: 4 }}>
            <Button block onClick={() => setCupomOpen(false)}>Agora não</Button>
            <Button block type="primary" loading={cupomSalvando} onClick={salvarCupom}>
              Salvar valores
            </Button>
          </div>
        }
      >
        {itensAguardando.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhum item aguardando valor." />
        ) : (
          <>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Copie do cupom o valor pago em cada item. O peso é opcional — preenchendo,
              o app calcula o preço por kg e passa a comparar mercados por ele.
            </Text>
            {itensAguardando.map((item) => {
              const dados = cupomValores[item.id] || {};
              const peso = dados.peso != null && dados.peso > 0 ? Number(dados.peso) : null;
              const porKg = dados.total != null && peso ? round2(dados.total / peso) : null;
              const setCampo = (campo, valor) =>
                setCupomValores((prev) => ({ ...prev, [item.id]: { ...prev[item.id], [campo]: valor } }));
              return (
                <div key={item.id} style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>{item.nome_produto}</div>
                  {(item.marca || item.conteudo) && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {[item.marca, item.conteudo].filter(Boolean).join(' · ')}
                    </Text>
                  )}
                  <Row gutter={8} style={{ marginTop: 8 }}>
                    <Col span={12}>
                      <InputNumber
                        {...MONEY}
                        size="large"
                        min={0}
                        placeholder="valor pago"
                        value={dados.total ?? null}
                        onChange={(v) => setCampo('total', v)}
                        style={{ width: '100%' }}
                      />
                      <div style={capStyle}>total do cupom</div>
                    </Col>
                    <Col span={12}>
                      <InputNumber
                        {...decimalInputProps(3, { min: 0 })}
                        size="large"
                        placeholder="peso"
                        value={dados.peso ?? null}
                        onChange={(v) => setCampo('peso', v)}
                        style={{ width: '100%' }}
                        addonAfter="kg"
                      />
                      <div style={capStyle}>opcional</div>
                    </Col>
                  </Row>
                  {porKg != null && (
                    <Text style={{ fontSize: 12, color: '#2F9E6F' }}>→ {brl(porKg)} por kg</Text>
                  )}
                </div>
              );
            })}
          </>
        )}
      </Drawer>

      <BarcodeScanner open={scannerOpen} onClose={() => setScannerOpen(false)} onDetected={aoEscanear} />

      {/* Modal "Ver todos" — todo o histórico (comprado ou só adicionado antes em outra
          lista de supermercado/atacadista), com busca e seleção múltipla. */}
      <Modal
        title={`Todos os produtos (${produtosDisponiveis.length})`}
        open={produtosModalOpen}
        onCancel={fecharProdutosModal}
        footer={[
          <Button key="cancel" onClick={fecharProdutosModal}>
            Cancelar
          </Button>,
          <Button
            key="add"
            type="primary"
            loading={adicionandoVarios}
            disabled={produtosSelecionados.size === 0}
            onClick={adicionarVariosProdutos}
          >
            Adicionar ({produtosSelecionados.size})
          </Button>,
        ]}
        destroyOnClose
      >
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: '#bbb' }} />}
          placeholder="Buscar produto..."
          value={produtosBusca}
          onChange={(e) => setProdutosBusca(e.target.value)}
          style={{ marginBottom: 12 }}
          autoFocus
        />
        <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {produtosModalFiltrados.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={produtosBusca.trim() ? 'Nenhum produto encontrado' : 'Nada por aqui ainda'}
            />
          ) : (
            <List
              dataSource={produtosModalFiltrados}
              renderItem={(p) => {
                const selecionado = produtosSelecionados.has(p.chave);
                return (
                  <List.Item
                    style={{ cursor: 'pointer', padding: '8px 4px' }}
                    onClick={() => toggleProdutoSelecionado(p.chave)}
                  >
                    <Checkbox checked={selecionado} style={{ marginRight: 10 }} />
                    <Text style={{ flex: 1 }}>{p.nome}</Text>
                    {selecionado && (
                      <span onClick={(e) => e.stopPropagation()} style={{ marginRight: 8 }}>
                        <InputNumber
                          {...qtdInputProps(p.unidade)}
                          size="small"
                          value={produtosQuantidades[p.chave] ?? 1}
                          onChange={(v) => setProdutosQuantidades((prev) => ({ ...prev, [p.chave]: v || 1 }))}
                          style={{ width: 64 }}
                        />
                      </span>
                    )}
                    <Text type="secondary" style={{ fontSize: 12 }}>{p.vezes}x</Text>
                  </List.Item>
                );
              }}
            />
          )}
        </div>
      </Modal>


      {alertasPreco.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            alertasPreco.length === 1
              ? `${alertasPreco[0].nome} está ${alertasPreco[0].acima_pct}% acima do que você costuma pagar (média ${brl(alertasPreco[0].preco_medio)}).`
              : `${alertasPreco.length} itens estão acima do preço que você costuma pagar.`
          }
        />
      )}

      {/* Barra: buscar produto na lista + organizar setores */}
      {itens.length > 0 && (
        <Row gutter={8} style={{ marginBottom: 8 }} align="middle">
          <Col flex="auto">
            <Input
              allowClear
              size={modoCompra ? 'large' : 'middle'}
              prefix={<SearchOutlined />}
              /* No Modo Compra esta é a única caixa de texto da tela: a de adicionar
                 produto virou bottom sheet. Antes as duas ficavam visíveis e parecidas,
                 e quem digitava aqui pra "achar" acabava criando um item duplicado. */
              placeholder={modoCompra ? 'Filtrar nesta lista...' : 'Buscar produto na lista...'}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </Col>
          {!lista.eh_modelo && !ehMarketplace && (
            <Col flex="0 0 auto">
              <Button
                size={modoCompra ? 'large' : 'middle'}
                icon={<AppstoreOutlined />}
                onClick={() => setSecoesOpen(true)}
              >
                Setores
              </Button>
            </Col>
          )}
        </Row>
      )}


      {/* Lista de itens */}
      <Card styles={{ body: { padding: 0 } }}>
        {itens.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <Text type="secondary">Nenhum item. {lista.eh_modelo ? '' : 'Adicione produtos acima.'}</Text>
          </div>
        ) : buscaAtiva ? (
          itensBusca.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <Text type="secondary">Nenhum item combina com “{busca.trim()}”.</Text>
            </div>
          ) : (
            <>
              <div style={{ padding: '8px 16px', background: '#FAFAFA', borderBottom: '1px solid #f0f0f0' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {itensBusca.length} {itensBusca.length === 1 ? 'resultado' : 'resultados'} para “{busca.trim()}”
                </Text>
              </div>
              {itensBusca.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  destaque={item.id === itemNovoId}
                  modoCompra={modoCompra}
                  bloqueada={bloqueada || lista.eh_modelo}
                  alertaPreco={alertaPrecoPorItem[item.id]}
                  secaoNome={ehMarketplace ? null : item.secao_nome}
                  ehMarketplace={ehMarketplace}
                  onToggleComprado={(checked) => alternarComprado(item, checked)}
                  onPrecoReal={(v) => precoConfirmado(item, v)}
                  onQtd={(v) => salvarItemCampo(item, { quantidade: v })}
                  onUnidade={(u) => salvarItemCampo(item, { unidade: u })}
                  onSemValor={() => confirmarSemValor(item)}
                  onIndisponivel={(motivo) => salvarItemCampo(item, { indisponivel: motivo })}
                  onEdit={() => abrirEditarItem(item)}
                  onSubstituir={ehMarketplace ? null : () => abrirSubstituir(item)}
                  onRemove={() => removerItem(item)}
                  onLancarItem={() => iniciarLancarItem(item)}
                  onVerificarPreco={() => abrirVerificarPreco(item)}
                  focoPreco={item.id === focoPrecoId}
                  onFocoConsumido={() => setFocoPrecoId((cur) => (cur === item.id ? null : cur))}
                />
              ))}
            </>
          )
        ) : grupos.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            {filtro === 'PENDENTES' ? (
              <>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#2F9E6F' }}>Tudo pego 🎉</div>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Confira os valores em &ldquo;Pegos&rdquo; e finalize a compra.
                </Text>
              </>
            ) : (
              <Text type="secondary">Nenhum item neste filtro.</Text>
            )}
          </div>
        ) : (
          grupos.map((g) => {
            const fechado = agrupar && gruposFechados.has(g.key);
            return (
              <div key={g.key}>
                {agrupar && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleGrupo(g.key)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleGrupo(g.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                      padding: '10px 16px', background: '#F5F0E6',
                      borderBottom: '1px solid #EADFC8', position: 'sticky', top: 0, zIndex: 1,
                    }}
                  >
                    {fechado ? <RightOutlined style={{ fontSize: 11 }} /> : <DownOutlined style={{ fontSize: 11 }} />}
                    <Text strong style={{ flex: 1, minWidth: 0 }} ellipsis>{g.nome}</Text>
                    <Text type="secondary" style={{ fontSize: 12, flex: '0 0 auto' }}>
                      {g.pendentes === 0
                        ? `tudo pego · ${g.total}`
                        : `${g.pendentes} a pegar · ${g.total}`}
                    </Text>
                  </div>
                )}
                {!fechado && g.itens.map((item) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    destaque={item.id === itemNovoId}
                    modoCompra={modoCompra}
                    bloqueada={bloqueada || lista.eh_modelo}
                    alertaPreco={alertaPrecoPorItem[item.id]}
                    secaoNome={ehMarketplace ? null : item.secao_nome}
                    ehMarketplace={ehMarketplace}
                    onToggleComprado={(checked) => alternarComprado(item, checked)}
                    onPrecoReal={(v) => precoConfirmado(item, v)}
                    onQtd={(v) => salvarItemCampo(item, { quantidade: v })}
                    onUnidade={(u) => salvarItemCampo(item, { unidade: u })}
                    onSemValor={() => confirmarSemValor(item)}
                    onIndisponivel={(motivo) => salvarItemCampo(item, { indisponivel: motivo })}
                    onEdit={() => abrirEditarItem(item)}
                  onSubstituir={ehMarketplace ? null : () => abrirSubstituir(item)}
                    onRemove={() => removerItem(item)}
                    onLancarItem={() => iniciarLancarItem(item)}
                    onVerificarPreco={() => abrirVerificarPreco(item)}
                    focoPreco={item.id === focoPrecoId}
                    onFocoConsumido={() => setFocoPrecoId((cur) => (cur === item.id ? null : cur))}
                  />
                ))}
              </div>
            );
          })
        )}
      </Card>

      {/* Barra de finalizar — fica visível o tempo todo durante o Modo Compra, sem
          precisar sair dele pra achar o botão de concluir/lançar. */}
      {modoCompra && (podeFinalizarNoModo || podeSoConcluirNoModo) && (
        <div
          style={{
            ...(isMobile
              ? {
                position: 'fixed',
                left: 0,
                right: 0,
                // Fica acima da barra de navegação inferior (fixa, z-index 950) — antes
                // ficava no mesmo bottom:0 e a nav cobria os botões de finalizar.
                bottom: 'calc(var(--mobile-bottom-nav-height, 56px) + env(safe-area-inset-bottom, 0px))',
                zIndex: 100,
                borderRadius: 0,
              }
              : { marginTop: 16, borderRadius: 8 }),
            background: '#fff',
            borderTop: '1px solid #e8e8e8',
            boxShadow: isMobile ? '0 -2px 10px rgba(0,0,0,0.1)' : '0 1px 4px rgba(0,0,0,0.06)',
            padding: isMobile ? '10px 12px' : '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          {/* O valor do carrinho já vive no cabeçalho fixo — repetir aqui deixava o mesmo
              número duas vezes na mesma tela. Aqui fica só o que falta e a ação. */}
          <div style={{ flex: '0 0 auto', minWidth: 72 }}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: 1.2 }}>
              {resumo.pendentes - resumo.naoEncontrados === 0
                ? 'tudo pego'
                : `faltam ${resumo.pendentes - resumo.naoEncontrados}`}
            </Text>
            <Text strong style={{ fontSize: 15, color: '#1F4F4F' }}>
              {resumo.comprados}/{resumo.total}
            </Text>
          </div>
          <div style={{ display: 'flex', gap: 8, flex: '1 1 auto', minWidth: 180 }}>
            {podeFinalizarNoModo && (
              <Button type="primary" size="large" icon={<CheckCircleOutlined />} onClick={abrirLancar} style={{ flex: 1 }}>
                Finalizar compra
              </Button>
            )}
            {podeSoConcluirNoModo && (
              <Dropdown
                trigger={['click']}
                placement="topRight"
                menu={{ items: [{ key: 'concluir', label: 'Só concluir (sem lançar)' }], onClick: () => concluirSemLancar() }}
              >
                <Button size="large" icon={<MoreOutlined />} />
              </Dropdown>
            )}
          </div>
        </div>
      )}

      {/* Ações da lista */}
      {!modoCompra && (
        <Card style={{ marginTop: 16 }}>
          <Space wrap>
            {!lista.eh_modelo && lista.status !== 'LANCADA' && (
              <Tooltip
                title={ehMarketplace ? 'Lança a lista inteira de uma vez — para itens já comprados um a um, use "Lançar este item" na linha de cada produto.' : undefined}
              >
                <Button type="primary" size="large" icon={<CheckCircleOutlined />} onClick={abrirLancar}>
                  {ehMarketplace ? 'Lançar tudo de uma vez' : 'Concluir e lançar no financeiro'}
                </Button>
              </Tooltip>
            )}
            {!lista.eh_modelo && !['LANCADA', 'CONCLUIDA'].includes(lista.status) && (
              <Button onClick={concluirSemLancar}>Só concluir (sem lançar)</Button>
            )}
            {lista.status === 'LANCADA' && lista.transacao && (
              <Button type="primary" onClick={() => navigate('/transacoes')}>Ver lançamento</Button>
            )}
            {!lista.eh_modelo && !ehMarketplace && itens.length > 0 && (
              <Button icon={<LineChartOutlined />} onClick={abrirComparador}>Comparar mercados</Button>
            )}
            <Button icon={<CopyOutlined />} onClick={duplicar}>Duplicar</Button>
            {!lista.eh_modelo && (
              <Button icon={<SaveOutlined />} onClick={salvarModelo}>Salvar como modelo</Button>
            )}
            <Button danger icon={<DeleteOutlined />} onClick={excluirLista}>Excluir lista</Button>
          </Space>
        </Card>
      )}

      <Modal
        title="Quanto custaria em cada mercado"
        open={comparadorOpen}
        onCancel={() => setComparadorOpen(false)}
        footer={[<Button key="c" onClick={() => setComparadorOpen(false)}>Fechar</Button>]}
      >
        {comparador && (comparador.local_generico || comparador.local_definido === false) && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="O comparador estima o custo desta lista em cada mercado do seu histórico"
            description="Ao concluir/lançar, informe a loja para o preço ser registrado no lugar certo."
          />
        )}
        {!comparador ? <Spin /> : comparador.por_local.length === 0 ? (
          <Text type="secondary">
            Sem histórico de preço nos seus mercados para os itens desta lista ainda.
            Conclua algumas compras primeiro.
          </Text>
        ) : (
          <>
            {comparador.melhor && (
              <Alert
                type="success"
                showIcon
                style={{ marginBottom: 12 }}
                message={`Mais barato no ${comparador.melhor.local} — economia de ${brl(comparador.melhor.economia)} vs o ${comparador.melhor.segundo}.`}
                description={comparador.melhor.itens_comuns > 0 && Number(comparador.melhor.economia_comum) > 0
                  ? `Nos ${comparador.melhor.itens_comuns} itens que você já comprou nos dois, a diferença é de ${brl(comparador.melhor.economia_comum)}.`
                  : undefined}
              />
            )}
            {comparador.por_local.map((r, i) => (
              <div key={r.local_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div>
                  <Text strong={i === 0}>{r.local}</Text>
                  <div><Text type="secondary" style={{ fontSize: 11 }}>
                    {r.itens_precificados}/{r.itens_total} itens com preço conhecido
                    {r.itens_estimados > 0 ? ' · demais pela sua média' : ''}
                    {r.generico ? ' · local genérico' : ''}
                  </Text></div>
                </div>
                <Text strong={i === 0} style={{ color: i === 0 ? '#2F9E6F' : undefined }}>{brl(r.total)}</Text>
              </div>
            ))}
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
              Todos os mercados são comparados na mesma cesta: último preço pago em cada um e,
              onde o item nunca foi comprado, a média do que você pagou nos outros.
              {comparador.itens_sem_historico > 0
                && ` ${comparador.itens_sem_historico} ${comparador.itens_sem_historico === 1 ? 'item ainda não tem' : 'itens ainda não têm'} preço em nenhum mercado e ${comparador.itens_sem_historico === 1 ? 'ficou' : 'ficaram'} de fora.`}
            </Text>
          </>
        )}
      </Modal>

      {/* Modal editar dados da lista */}
      <Modal
        title="Editar lista"
        open={metaOpen}
        onCancel={() => setMetaOpen(false)}
        onOk={() => metaForm.submit()}
        confirmLoading={saving}
        okText="Salvar"
        destroyOnClose
      >
        <Form form={metaForm} layout="vertical" onFinish={salvarMeta}>
          <Form.Item name="titulo" label="Nome da lista">
            <Input placeholder="Ex.: Compra da semana" maxLength={120} />
          </Form.Item>
          <Form.Item name="data_compra" label="Data da compra">
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="observacao" label="Observação">
            <Input.TextArea rows={2} maxLength={500} placeholder="Notas opcionais" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal substituir item */}
      <Modal
        title={substItem ? `Substituir “${substItem.nome_produto}”` : 'Substituir item'}
        open={!!substItem}
        onCancel={() => setSubstItem(null)}
        onOk={() => substForm.submit()}
        confirmLoading={saving}
        okText="Substituir"
        destroyOnClose
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
          Escaneie ou digite o produto que você pegou. Quantidade, unidade e setor continuam
          os mesmos.
        </Text>
        <Button
          block
          size="large"
          icon={<BarcodeOutlined />}
          onClick={() => abrirScanner('substituir')}
          style={{ marginBottom: 12 }}
        >
          Escanear código de barras
        </Button>
        <Form form={substForm} layout="vertical" onFinish={salvarSubstituicao}>
          <Form.Item name="nome_produto" label="Produto" rules={[{ required: true, message: 'Informe o produto' }]}>
            <Input
              maxLength={160}
              placeholder={substItem ? `Ex.: ${substItem.nome_produto} da marca X` : ''}
              onChange={() => { if (substGtin) setSubstGtin(''); }}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="marca" label="Marca">
                <Input placeholder="Ex.: Tio João" maxLength={80} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="conteudo" label="Tamanho da embalagem">
                <Input placeholder="Ex.: 5kg, 1L" maxLength={40} />
              </Form.Item>
            </Col>
          </Row>
          {modoCompra && (
            <Form.Item
              name="preco_pago"
              label="Preço pago (opcional)"
              extra="Preenchido, o item já vai pro carrinho."
            >
              <InputNumber {...MONEY} style={{ width: '100%' }} placeholder="preço pago" />
            </Form.Item>
          )}
          {substGtin && (
            <Text type="secondary" style={{ fontSize: 11 }}>Código de barras: {substGtin}</Text>
          )}
        </Form>
      </Modal>

      {/* Modal editar item */}
      <Modal
        title="Editar item"
        open={!!editItem}
        onCancel={() => setEditItem(null)}
        onOk={() => editForm.submit()}
        confirmLoading={saving}
        okText="Salvar"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={salvarEdicaoItem}>
          <Form.Item name="nome_produto" label="Produto" rules={[{ required: true }]}>
            <Input maxLength={160} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="marca" label="Marca">
                <Input placeholder="Ex.: Nestlé" maxLength={80} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="conteudo" label="Tamanho da embalagem">
                <Input placeholder="Ex.: 500g, 1L" maxLength={40} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="quantidade" label="Quantidade">
                <InputNumber
                  {...qtdInputProps(unidadeEditWatch || editItem?.unidade)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="unidade" label="Unidade">
                <Select options={UNIDADES} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="preco_estimado" label="Preço estimado (unitário)">
            <InputNumber {...MONEY} min={0} />
          </Form.Item>
          {!ehMarketplace && (
            <Form.Item
              name="secao"
              label="Setor (corredor)"
              tooltip="Onde o produto fica na loja. Sua escolha é lembrada para as próximas listas."
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Automático pelo nome"
                options={secoes.map((s) => ({ value: s.id, label: s.nome }))}
              />
            </Form.Item>
          )}
          {ehMarketplace && (
            <>
              <Form.Item name="link_produto" label="Link do produto">
                <Input placeholder="https://..." maxLength={500} prefix={<LinkOutlined />} />
              </Form.Item>
              <Form.Item
                name="prioridade"
                label="Prioridade"
                tooltip="Itens de prioridade alta aparecem primeiro na lista — ajuda a decidir o que comprar primeiro quando o orçamento é curto."
              >
                <Select allowClear placeholder="Sem prioridade" options={PRIORIDADES} />
              </Form.Item>

              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                <Text style={{ fontSize: 13 }}>Pausa anti-impulso</Text>
                <Tooltip title="Até essa data, o app confirma antes de marcar como comprado ou lançar — um tempo pra pensar se vale a pena. Não impede comprar antes, só confirma.">
                  <QuestionCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                </Tooltip>
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                {[3, 7, 15, 30].map((n) => {
                  const ativo = disponivelEditWatch && dayjs(disponivelEditWatch).isSame(dayjs().add(n, 'day'), 'day');
                  return (
                    <Button
                      key={n}
                      size="small"
                      type={ativo ? 'primary' : 'default'}
                      onClick={() => editForm.setFieldsValue({ disponivel_em: dayjs().add(n, 'day') })}
                    >
                      +{n}d
                    </Button>
                  );
                })}
                {disponivelEditWatch && (
                  <Button size="small" type="text" onClick={() => editForm.setFieldsValue({ disponivel_em: null })}>
                    limpar
                  </Button>
                )}
              </div>
              <Form.Item name="disponivel_em" style={{ marginBottom: 8 }}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" allowClear placeholder="ou escolha outra data" />
              </Form.Item>
            </>
          )}
          <Form.Item name="observacao" label="Observação / alternativas">
            <Input placeholder="marca, tipo, comparação com outra opção..." maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal setores (corredores) */}
      <Modal
        title="Setores da loja"
        open={secoesOpen}
        onCancel={fecharSecoes}
        footer={[<Button key="ok" type="primary" onClick={fecharSecoes}>Concluído</Button>]}
      >
        <Text type="secondary">
          Coloque na ordem em que você passa pelos corredores — os itens da lista se agrupam
          por aqui. Renomear ou remover vale para todas as suas listas.
        </Text>
        <div style={{ marginTop: 12, marginBottom: 12 }}>
          {secoes.map((s, idx) => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
              <Button
                size="small"
                type="text"
                icon={<ArrowUpOutlined />}
                disabled={idx === 0}
                onClick={() => moverSecao(idx, -1)}
              />
              <Button
                size="small"
                type="text"
                icon={<ArrowDownOutlined />}
                disabled={idx === secoes.length - 1}
                onClick={() => moverSecao(idx, 1)}
              />
              <Input
                size="small"
                defaultValue={s.nome}
                style={{ flex: 1 }}
                onBlur={(e) => renomearSecao(s, e.target.value)}
                onPressEnter={(e) => e.target.blur()}
              />
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removerSecao(s)}
              />
            </div>
          ))}
        </div>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="Novo setor (ex.: Corredor 7, Frios)"
            maxLength={60}
            value={novoSetor}
            onChange={(e) => setNovoSetor(e.target.value)}
            onPressEnter={() => { adicionarSecao(novoSetor); setNovoSetor(''); }}
          />
          <Button type="primary" onClick={() => { adicionarSecao(novoSetor); setNovoSetor(''); }}>
            Adicionar
          </Button>
        </Space.Compact>
      </Modal>

      {/* Modal lançar */}
      <Modal
        title="Lançar compra no financeiro"
        open={lancarOpen}
        onCancel={() => setLancarOpen(false)}
        onOk={() => lancarForm.submit()}
        confirmLoading={saving}
        okText="Lançar"
        destroyOnClose
      >
        <Form form={lancarForm} layout="vertical" onFinish={confirmarLancar}>
          {localGenerico && (
            <Form.Item
              name="estabelecimento"
              label="Em qual loja foi a compra?"
              rules={[{ required: true, message: 'Informe a loja.' }]}
              extra={
                localGenerico.definido
                  ? `“${localGenerico.nome}” é genérico — o comparador precisa da loja específica.`
                  : 'Esta lista não tem local — o histórico de preço fica ligado à loja informada.'
              }
            >
              <AutoComplete
                placeholder="Ex.: Assaí Aterrado, Extra Tijuca"
                options={localGenerico.sugestoes.map((s) => ({ value: s }))}
              />
            </Form.Item>
          )}
          <CamposLancamento
            form={lancarForm}
            despesaCategorias={despesaCategorias}
            contas={contas}
            cartoes={cartoes}
            faturas={faturas}
            faturasLoading={faturasLoading}
            onCartaoChange={carregarFaturas}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            A lista fica marcada como “Lançada”. Editar o lançamento depois não altera a lista.
          </Text>
        </Form>
      </Modal>

      {/* Modal lançar item individual (wishlist de marketplace) */}
      <Modal
        title="Lançar este item"
        open={itemLancarOpen}
        onCancel={() => { setItemLancarOpen(false); setItemLancarAlvo(null); }}
        onOk={() => itemLancarForm.submit()}
        confirmLoading={saving}
        okText="Lançar"
        destroyOnClose
      >
        <Form form={itemLancarForm} layout="vertical" onFinish={confirmarLancarItem}>
          <CamposLancamento
            form={itemLancarForm}
            despesaCategorias={despesaCategorias}
            contas={contas}
            cartoes={cartoes}
            faturas={itemFaturas}
            faturasLoading={itemFaturasLoading}
            onCartaoChange={carregarFaturasItem}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Lança só este item — os demais itens da lista continuam disponíveis pra comprar depois.
          </Text>
        </Form>
      </Modal>

      {/* Modal verificar preço (link do marketplace) */}
      <Modal
        title="Verificar preço"
        open={!!verificarItem}
        onCancel={() => setVerificarItem(null)}
        onOk={() => verificarForm.submit()}
        confirmLoading={saving}
        okText="Salvar"
        destroyOnClose
      >
        {verificarItem?.link_produto && (
          <div style={{ marginBottom: 12 }}>
            <a href={verificarItem.link_produto} target="_blank" rel="noopener noreferrer">
              <LinkOutlined /> Abrir o produto no marketplace
            </a>
          </div>
        )}
        <Form form={verificarForm} layout="vertical" onFinish={confirmarVerificarPreco}>
          <Form.Item name="preco" label="Preço encontrado agora" rules={[{ required: true }]}>
            <InputNumber {...MONEY} min={0} style={{ width: '100%' }} autoFocus />
          </Form.Item>
          <Form.Item name="data" label="Data da verificação" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
        </Form>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Fica registrado no histórico de preço do produto — mesmo gráfico usado nas compras normais.
        </Text>
      </Modal>
    </Content>
  );
}

function CamposLancamento({ form, despesaCategorias, contas, cartoes, faturas, faturasLoading, onCartaoChange }) {
  const formaPagamento = Form.useWatch('forma_pagamento', form);
  return (
    <>
      <Form.Item name="descricao" label="Descrição" rules={[{ required: true }]}>
        <Input maxLength={255} />
      </Form.Item>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="valor" label="Valor" rules={[{ required: true }]}>
            <InputNumber {...MONEY} min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="data" label="Data" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="categoria" label="Categoria" rules={[{ required: true, message: 'Escolha a categoria.' }]}>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Categoria de despesa"
          options={despesaCategorias.map((c) => ({ value: c.id, label: c.nome }))}
        />
      </Form.Item>
      <Form.Item name="forma_pagamento" label="Forma de pagamento">
        <Radio.Group>
          <Radio value="conta">Conta</Radio>
          <Radio value="cartao">Cartão</Radio>
        </Radio.Group>
      </Form.Item>
      {formaPagamento === 'cartao' ? (
        <>
          <Form.Item name="cartao" label="Cartão" rules={[{ required: true, message: 'Escolha o cartão.' }]}>
            <Select
              placeholder="Cartão de crédito"
              onChange={onCartaoChange}
              options={cartoes.map((c) => ({ value: c.id, label: c.nome }))}
            />
          </Form.Item>
          <Form.Item
            name="fatura"
            label="Fatura"
            rules={[{ required: true, message: 'Escolha a fatura.' }]}
            tooltip="Você pode lançar numa fatura já fechada, se precisar."
          >
            <Select
              placeholder="Escolha o cartão primeiro"
              loading={faturasLoading}
              disabled={faturas.length === 0}
              options={faturas.map((f) => ({
                value: f.id,
                label: `${String(f.mes_referencia).padStart(2, '0')}/${f.ano_referencia} · ${STATUS_FATURA[f.status] || f.status}`,
              }))}
            />
          </Form.Item>
        </>
      ) : (
        <>
          <Form.Item name="conta" label="Conta" rules={[{ required: true, message: 'Escolha a conta.' }]}>
            <Select
              placeholder="Conta"
              options={contas.map((c) => ({
                value: c.id,
                label: `${c.nome} · ${brl(c.saldo_atual)}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="status" label="Situação">
            <Radio.Group>
              <Radio value="PAGO">Paga</Radio>
              <Radio value="PENDENTE">Pendente</Radio>
            </Radio.Group>
          </Form.Item>
        </>
      )}

      <Divider style={{ margin: '8px 0' }} />
      <Form.Item name="parcelas" label="Parcelas" style={{ marginBottom: 8 }}>
        <InputNumber min={1} max={60} style={{ width: 120 }} addonAfter="x" />
      </Form.Item>
    </>
  );
}

function ItemRow({
  item, destaque, modoCompra, bloqueada, alertaPreco, secaoNome, ehMarketplace, focoPreco,
  onToggleComprado, onPrecoReal, onQtd, onIndisponivel, onEdit, onRemove, onLancarItem,
  onVerificarPreco, onFocoConsumido, onUnidade, onSemValor, onSubstituir,
}) {
  const qty = Number(item.quantidade) || 1;
  const unitInicial = item.preco_unitario != null ? Number(item.preco_unitario) : null;
  const [precoReal, setPrecoReal] = useState(unitInicial);
  const [totalReal, setTotalReal] = useState(unitInicial != null ? round2(unitInicial * qty) : null);
  const [qtdLocal, setQtdLocal] = useState(qty);
  // Qual dos dois números o usuário está digitando. Antes o campo visível era sempre o
  // preço por unidade e o total ficava atrás de um link de 11px ("+ total da linha") —
  // mas na gôndola o que se lê é justamente o total da embalagem.
  // Para kg/L o campo que abre é o TOTAL: na balança do hortifrúti o adesivo traz o
  // valor pago, e o preço por kg é o que se deduz dele — não o contrário.
  const [basePreco, setBasePreco] = useState(
    UNIDADES_FRACIONARIAS.has(item.unidade) ? 'TOTAL' : 'UN',
  );
  const precoRef = useRef(null);

  useEffect(() => {
    const u = item.preco_unitario != null ? Number(item.preco_unitario) : null;
    setPrecoReal(u);
    setTotalReal(u != null ? round2(u * qty) : null);
    setQtdLocal(qty);
  }, [item.preco_unitario, qty]);

  useEffect(() => {
    setBasePreco(UNIDADES_FRACIONARIAS.has(item.unidade) ? 'TOTAL' : 'UN');
  }, [item.unidade]);

  // Marcar o item como pego traz o foco pra cá e abre o teclado numérico: um gesto só,
  // em vez de marcar num canto da linha e caçar o campo de preço no outro.
  useEffect(() => {
    if (!focoPreco) return undefined;
    const t = setTimeout(() => precoRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [focoPreco]);

  const commitQtd = (valor) => {
    const v = valor ?? qtdLocal;
    if (v != null && v > 0 && v !== Number(item.quantidade)) onQtd(v);
    else if (v == null || v <= 0) setQtdLocal(Number(item.quantidade));
  };

  const passo = (delta) => {
    const v = round2(Math.max(1, (Number(qtdLocal) || 0) + delta));
    setQtdLocal(v);
    commitQtd(v);
    navigator.vibrate?.(8);
  };

  const subtotal = Number(item.subtotal_final) > 0 ? item.subtotal_final : item.subtotal_estimado;
  const unidadeLabel = (UNIDADES.find((u) => u.value === item.unidade) || {}).label || item.unidade;
  const mostrarTotal = Math.abs(qty - 1) > 1e-9;
  const fracionaria = UNIDADES_FRACIONARIAS.has(item.unidade);

  const commitUnit = (u) => {
    if (u !== unitInicial) onPrecoReal(u);
    onFocoConsumido?.();
  };
  const aoMudarPreco = (u) => {
    setPrecoReal(u);
    setTotalReal(u != null ? round2(u * qty) : null);
  };
  const aoMudarTotal = (t) => {
    setTotalReal(t);
    setPrecoReal(t != null && qty > 0 ? round2(t / qty) : null);
  };

  const podePrecificar = !bloqueada || item.preco_unitario != null;
  // Na gôndola, na maioria das vezes o preço bate com o estimado — 1 toque confirma o
  // item com ele; só digita quem encontrou um valor diferente.
  const estimado = item.preco_estimado != null ? Number(item.preco_estimado) : null;
  const podeUsarEstimado = modoCompra && !item.comprado && item.preco_unitario == null
    && estimado > 0 && !bloqueada && !item.indisponivel;
  const usarEstimado = (e) => {
    e?.stopPropagation();
    onPrecoReal(estimado);
    onFocoConsumido?.();
  };
  const botaoUsarEstimado = podeUsarEstimado && (
    <Button
      size="small"
      type="primary"
      ghost
      icon={<CheckOutlined />}
      onClick={usarEstimado}
      style={{ fontSize: 12 }}
      title="Marcar como pego com o preço estimado"
    >
      Usar estimado {brl(round2(estimado * qty))}
    </Button>
  );
  // Item já lançado individualmente (marketplace) — os valores ficam fixos, editar aqui
  // não atualizaria retroativamente o lançamento já criado no financeiro.
  const travadoPorLancamento = ehMarketplace && !!item.transacao;
  // Pego, mas o valor só sai no caixa. Deixou de ser acidente: só se chega aqui pela
  // ação explícita "valor sai no caixa", e o item fica rotulado até o cupom ser conferido.
  const aguardandoValor = item.comprado && item.preco_unitario == null;

  const PF = 130; // largura fixa dos campos de preço (não estica no desktop)

  const podeSubstituir = !!onSubstituir && !item.transacao;
  const menuIndisponivel = {
    items: [
      ...(podeSubstituir
        ? [{ key: 'substituir', label: 'Substituir por outro produto', icon: <SwapOutlined /> }, { type: 'divider' }]
        : []),
      { key: 'NAO_ENCONTRADO', label: 'Não encontrei' },
      { key: 'MUITO_CARO', label: 'Muito caro' },
      ...(item.indisponivel ? [{ type: 'divider' }, { key: '', label: 'Voltar para pendente' }] : []),
    ],
    onClick: ({ key }) => (key === 'substituir' ? onSubstituir() : onIndisponivel(key)),
  };

  // Menu do Modo Compra. Corrigir o item (principalmente trocar un ↔ kg, que só se
  // percebe na frente da gôndola) exigia sair do modo compra e voltar — agora resolve
  // aqui, sem perder o lugar na lista.
  const menuItemCompra = {
    items: [
      {
        key: 'unidade',
        label: `Unidade: ${unidadeLabel}`,
        children: UNIDADES.map((u) => ({
          key: `unid:${u.value}`,
          label: u.label,
          disabled: u.value === item.unidade,
        })),
      },
      { key: 'editar', label: 'Editar item' },
      ...(podeSubstituir ? [{ key: 'substituir', label: 'Substituir por outro produto', icon: <SwapOutlined /> }] : []),
      ...(item.comprado
        ? []
        : [
          { type: 'divider' },
          { key: 'sem_valor', label: 'Peguei — valor sai no caixa' },
          { key: 'NAO_ENCONTRADO', label: 'Não encontrei' },
          { key: 'MUITO_CARO', label: 'Muito caro' },
        ]),
      ...(item.indisponivel ? [{ key: '', label: 'Voltar para pendente' }] : []),
      { type: 'divider' },
      { key: 'remover', label: 'Remover da lista', danger: true },
    ],
    onClick: ({ key }) => {
      if (key === 'editar') return onEdit();
      if (key === 'substituir') return onSubstituir();
      if (key === 'remover') return onRemove();
      if (key === 'sem_valor') return onSemValor?.();
      if (key.startsWith('unid:')) return onUnidade?.(key.slice(5));
      return onIndisponivel(key);
    },
  };

  // --- Modo Compra: linha desenhada pra uma mão, empurrando carrinho ---
  if (modoCompra) {
    const pego = !!item.comprado;
    return (
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid #f0f0f0',
          // O estado "pego" agora é um fundo verde com check preenchido, não um
          // opacity: 0.6 que quase não se lê com a tela na claridade do mercado.
          background: pego ? '#EBF7F1' : item.indisponivel ? '#FBF3EC' : undefined,
          animation: destaque ? 'itemNovoFlash 2.4s ease-out' : undefined,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          {/* Alvo de toque de 40px: marcar/desmarcar. Antes era um checkbox de 14px
              colado no botão "não encontrei" — dois alvos pequenos e vizinhos com
              efeitos opostos. */}
          <div
            role="button"
            tabIndex={0}
            aria-label={pego ? 'Desmarcar item' : 'Marcar como pego'}
            title={pego ? 'Desmarcar' : 'Informe o preço pra confirmar que pegou'}
            onClick={() => !bloqueada && !travadoPorLancamento && onToggleComprado(!pego)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !bloqueada && onToggleComprado(!pego)}
            style={{
              flex: '0 0 auto', width: 40, height: 40, marginLeft: -4,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: bloqueada || travadoPorLancamento ? 'default' : 'pointer',
            }}
          >
            <div
              style={{
                width: 28, height: 28, borderRadius: '50%',
                border: pego ? '2px solid #2F9E6F' : '2px solid #C9C2B4',
                background: pego ? '#2F9E6F' : '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.15s ease, border-color 0.15s ease',
              }}
            >
              {pego && <CheckCircleOutlined style={{ color: '#fff', fontSize: 16 }} />}
            </div>
          </div>

          <div
            onClick={() => !bloqueada && !travadoPorLancamento && onToggleComprado(!pego)}
            style={{ flex: 1, minWidth: 0, cursor: bloqueada ? undefined : 'pointer', paddingTop: 2 }}
          >
            <div style={{
              fontSize: 16, fontWeight: 600, lineHeight: 1.3,
              textDecoration: pego ? 'line-through' : 'none',
              color: pego ? '#5A6A6A' : undefined,
              overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            }}>
              {item.nome_produto}
            </div>
            {(item.marca || item.conteudo || secaoNome) && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {[item.marca, item.conteudo, secaoNome].filter(Boolean).join(' · ')}
              </Text>
            )}
          </div>

          {/* O menu fica disponível também em item já pego — corrigir unidade ou valor
              depois de marcar é justamente o caso mais comum. */}
          <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 4 }}>
            {item.indisponivel && (
              <Tag color={item.indisponivel === 'MUITO_CARO' ? 'red' : 'orange'} style={{ margin: 0 }}>
                {item.indisponivel_label}
              </Tag>
            )}
            {!bloqueada && (
              <Dropdown trigger={['click']} menu={menuItemCompra}>
                <Button type="text" icon={<MoreOutlined />} style={{ width: 36, height: 36 }} title="Editar, trocar unidade, não encontrei" />
              </Dropdown>
            )}
          </div>
        </div>

        {/* Linha de quantidade + preço. Fica sempre visível e alinhada sob o nome —
            antes o bloco de preço quebrava pra outra linha e se alinhava à direita,
            longe do item a que pertence. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, paddingLeft: 40, flexWrap: 'wrap' }}>
          {fracionaria ? (
            <InputNumber
              {...qtdInputProps(item.unidade)}
              size="large"
              value={qtdLocal}
              disabled={bloqueada || travadoPorLancamento}
              style={{ width: 120 }}
              onChange={setQtdLocal}
              onBlur={() => commitQtd()}
              addonAfter={unidadeLabel}
            />
          ) : (
            /* Stepper: alvos de 40px pra ajustar quantidade com uma mão só. */
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden', flex: '0 0 auto' }}>
              <Button
                type="text"
                icon={<MinusOutlined />}
                disabled={bloqueada || travadoPorLancamento || qtdLocal <= 1}
                onClick={() => passo(-1)}
                style={{ width: 40, height: 40, borderRadius: 0 }}
              />
              <div style={{ minWidth: 44, textAlign: 'center', fontSize: 16, fontWeight: 600 }}>
                {qtdLocal} <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>{unidadeLabel}</Text>
              </div>
              <Button
                type="text"
                icon={<PlusOutlined />}
                disabled={bloqueada || travadoPorLancamento}
                onClick={() => passo(1)}
                style={{ width: 40, height: 40, borderRadius: 0 }}
              />
            </div>
          )}

          {podePrecificar && (
            <div style={{ flex: '1 1 120px', minWidth: 110 }}>
              <InputNumber
                {...MONEY}
                ref={precoRef}
                size="large"
                min={0}
                placeholder={basePreco === 'TOTAL' ? 'total pago' : 'preço'}
                value={basePreco === 'TOTAL' ? totalReal : precoReal}
                disabled={bloqueada || travadoPorLancamento}
                status={aguardandoValor ? 'warning' : undefined}
                style={{ width: '100%' }}
                onChange={basePreco === 'TOTAL' ? aoMudarTotal : aoMudarPreco}
                onPressEnter={(e) => e.target.blur()}
                onBlur={() => commitUnit(
                  basePreco === 'TOTAL'
                    ? (totalReal != null && qty > 0 ? round2(totalReal / qty) : null)
                    : precoReal,
                )}
              />
            </div>
          )}

          {podePrecificar && mostrarTotal && (
            /* Escolha explícita entre os dois números, em vez do link escondido de 11px:
               na gôndola às vezes se lê o preço do kg, às vezes o total da embalagem. */
            <Segmented
              size="small"
              value={basePreco}
              onChange={setBasePreco}
              options={[
                { value: 'UN', label: precoUnidadeLabel(item.unidade).replace('por ', '/') },
                { value: 'TOTAL', label: 'total' },
              ]}
            />
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, paddingLeft: 40, flexWrap: 'wrap' }}>
          {aguardandoValor ? (
            <Tag color="gold" style={{ margin: 0 }}>valor no caixa</Tag>
          ) : !pego && focoPreco ? (
            /* A saída pro hortifrúti fica exatamente onde a dúvida aparece: o usuário
               tocou pra confirmar, não sabe o preço, e o botão está ali do lado. */
            <>
              <Text style={{ fontSize: 12, color: '#B8860B' }}>
                {basePreco === 'TOTAL' ? 'Digite o total pago' : 'Digite o preço'} pra confirmar.
              </Text>
              {botaoUsarEstimado}
              <Button
                size="small"
                onClick={() => onSemValor?.()}
                style={{ fontSize: 12 }}
              >
                Valor sai no caixa
              </Button>
            </>
          ) : (
            <>
              <Text strong style={{ fontSize: 14, color: pego ? '#2F9E6F' : '#1F4F4F' }}>{brl(subtotal)}</Text>
              {botaoUsarEstimado || (item.preco_estimado != null && (
                <Text type="secondary" style={{ fontSize: 12 }}>est. {brl(item.preco_estimado)}</Text>
              ))}
            </>
          )}
          {item.observacao && <Text type="secondary" style={{ fontSize: 12 }}>{item.observacao}</Text>}
          {alertaPreco && (
            <Text style={{ fontSize: 12, color: '#D64545' }}>
              ↑ {alertaPreco.acima_pct}% acima da sua média ({brl(alertaPreco.preco_medio)})
            </Text>
          )}
        </div>
      </div>
    );
  }

  // --- Fora do Modo Compra: montar/revisar a lista (mouse, tela larga) ---
  return (
    <div
      style={{
        padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0',
        opacity: item.comprado || item.indisponivel ? 0.6 : 1,
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        animation: destaque ? 'itemNovoFlash 2.4s ease-out' : undefined,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: '1 1 240px', minWidth: 0 }}>
        <Checkbox
          checked={item.comprado}
          disabled={bloqueada || travadoPorLancamento}
          onChange={(e) => onToggleComprado(e.target.checked)}
          style={{ marginTop: 3 }}
        />
        {!bloqueada && !item.comprado && (
          <div>
            <Dropdown trigger={['click']} menu={menuIndisponivel}>
              {item.indisponivel ? (
                <Tag
                  color={item.indisponivel === 'MUITO_CARO' ? 'red' : 'orange'}
                  style={{ cursor: 'pointer', marginTop: 1 }}
                >
                  {item.indisponivel_label}
                </Tag>
              ) : (
                <Button
                  type="text"
                  size="small"
                  icon={<MoreOutlined />}
                  style={{ marginTop: -4 }}
                  title="Não encontrei / muito caro"
                />
              )}
            </Dropdown>
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.3,
            textDecoration: item.comprado ? 'line-through' : 'none',
            overflow: 'hidden', textOverflow: 'ellipsis',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {item.nome_produto}
          </div>
          {(item.marca || item.conteudo) ? (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {[item.marca, item.conteudo].filter(Boolean).join(' · ')}
            </Text>
          ) : null}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            <InputNumber
              {...qtdInputProps(item.unidade)}
              size="small"
              value={qtdLocal}
              disabled={bloqueada || travadoPorLancamento}
              style={{ width: fracionaria ? 88 : 64 }}
              onChange={setQtdLocal}
              onBlur={() => commitQtd()}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {unidadeLabel}
              {item.preco_estimado != null && ` · est. ${brl(item.preco_estimado)}`}
              {item.observacao ? ` · ${item.observacao}` : ''}
            </Text>
            {secaoNome && (
              <Tag style={{ marginLeft: 6, fontSize: 10, lineHeight: '16px', padding: '0 5px' }}>{secaoNome}</Tag>
            )}
          </div>
          {ehMarketplace && (item.prioridade || item.link_produto || emReflexao(item)) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
              {item.prioridade && (
                <Tooltip title="Itens de prioridade alta aparecem primeiro na lista.">
                  <Tag
                    color={(PRIORIDADES.find((p) => p.value === item.prioridade) || {}).color}
                    style={{ fontSize: 10, lineHeight: '16px', padding: '0 5px', cursor: 'help' }}
                  >
                    {item.prioridade_label}
                  </Tag>
                </Tooltip>
              )}
              {item.link_produto && (
                <a href={item.link_produto} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11 }}>
                  <LinkOutlined /> produto
                </a>
              )}
              {emReflexao(item) && (
                <Tooltip title="Pausa anti-impulso: o app confirma antes de marcar como comprado ou lançar, até essa data.">
                  <Tag color="orange" style={{ fontSize: 10, lineHeight: '16px', padding: '0 5px', cursor: 'help' }}>
                    <ClockCircleOutlined /> aguardar até {fmtData(item.disponivel_em)}
                  </Tag>
                </Tooltip>
              )}
            </div>
          )}
          {aguardandoValor && (
            <div style={{ fontSize: 11, color: '#B8860B', marginTop: 2 }}>
              Aguardando o valor do cupom — entra como R$ 0,00 até ser preenchido.
            </div>
          )}
          {alertaPreco && (
            <div style={{ fontSize: 11, color: '#D64545', marginTop: 2 }}>
              ↑ {alertaPreco.acima_pct}% acima da sua média ({brl(alertaPreco.preco_medio)})
            </div>
          )}
        </div>
      </div>

      {/* bloco direito — preços · subtotal · ações. Em telas estreitas, o bloco
          esquerdo (nome) já consome a largura toda e este acaba quebrando pra uma
          nova linha — className garante que, quebrado, ele ocupe a linha inteira
          alinhado à direita em vez de ficar pendurado embaixo do checkbox. */}
      <div className="sa-item-preco-bloco" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
        {podePrecificar && (
          <div style={{ width: PF }}>
            <InputNumber
              {...MONEY}
              min={0}
              placeholder="preço"
              value={precoReal}
              disabled={bloqueada || travadoPorLancamento}
              status={aguardandoValor ? 'warning' : undefined}
              style={{ width: '100%' }}
              onChange={aoMudarPreco}
              onBlur={() => commitUnit(precoReal)}
            />
            <div style={capStyle}>{precoUnidadeLabel(item.unidade)}</div>
          </div>
        )}

        {podePrecificar && mostrarTotal && (
          <div style={{ width: PF }}>
            <InputNumber
              {...MONEY}
              min={0}
              placeholder="total pago"
              value={totalReal}
              disabled={bloqueada || travadoPorLancamento}
              style={{ width: '100%' }}
              onChange={aoMudarTotal}
              onBlur={() => commitUnit(totalReal != null && qty > 0 ? round2(totalReal / qty) : null)}
            />
            <div style={capStyle}>total do produto</div>
          </div>
        )}

        <div style={{ minWidth: 78, textAlign: 'right', fontWeight: 700, fontSize: 14, paddingTop: podePrecificar ? 6 : 2 }}>
          {brl(subtotal)}
        </div>

        {!bloqueada && (
          <Space size={0} style={{ paddingTop: podePrecificar ? 2 : 0 }}>
            {ehMarketplace && !item.transacao && (
              <Tooltip title="Verificar preço">
                <Button type="text" size="small" icon={<DollarOutlined />} onClick={onVerificarPreco} />
              </Tooltip>
            )}
            {ehMarketplace && !item.transacao && (
              <Tooltip title="Lançar este item">
                <Button type="text" size="small" icon={<CheckCircleOutlined />} onClick={onLancarItem} />
              </Tooltip>
            )}
            {ehMarketplace && item.transacao ? (
              <Tag color="green" style={{ marginLeft: 4 }}>Lançado ✓</Tag>
            ) : (
              <>
                <Tooltip title="Editar"><Button type="text" size="small" icon={<EditOutlined />} onClick={onEdit} /></Tooltip>
                <Tooltip title="Remover"><Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={onRemove} /></Tooltip>
              </>
            )}
          </Space>
        )}
      </div>
    </div>
  );
}

export default ListaCompraDetailPage;
